import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FaBookOpen,
    FaChalkboardTeacher,
    FaChevronDown,
    FaChevronUp,
    FaComment,
    FaHeart,
    FaPaperPlane,
    FaQuestionCircle,
    FaReply,
    FaSmile,
    FaThumbsUp,
    FaUser,
    FaUsers,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../api/api";
import defaultAvatar from "./learningenvironment.jpg";
import StudentPersonalBio from "./StudentPersonalBio";

const MANAGEMENT_ROLES = new Set([
    "teacher",
    "facilitator",
    "instructor",
    "admin",
    "administrator",
    "principal",
    "proprietor",
]);

const MAX_QUESTION_LENGTH = 3000;
const MAX_REPLY_LENGTH = 3000;
const INITIAL_REPLIES_VISIBLE = 5;
const REPLIES_LOAD_STEP = 5;

const REACTION_OPTIONS = [
    {
        type: "like",
        label: "Like",
        icon: <FaThumbsUp />,
        className: "like",
    },
    {
        type: "love",
        label: "Love",
        icon: <FaHeart />,
        className: "love",
    },
    {
        type: "helpful",
        label: "Helpful",
        icon: <FaSmile />,
        className: "helpful",
    },
    {
        type: "celebrate",
        label: "Celebrate",
        icon: <FaSmile />,
        className: "celebrate",
    },
];

const QUESTION_AND_ANSWER_STYLES = `
.cayep-community-root {
    --cayep-nav-navy: #062A4B;
    --cayep-nav-blue: #063A6B;
    --cayep-nav-black: #0A0F12;
    --cayep-nav-gold: #F6C944;
    --cayep-nav-yellow: #EFA81C;
    --cayep-nav-orange: #C0750D;
    --cayep-nav-green: #4FAE22;
    --cayep-nav-olive: #5F6622;
    --cayep-nav-sky: #0F6DAB;
    --cayep-nav-white: #F0EDEA;
    --cayep-nav-gray: #C4C7C7;
    --cayep-nav-antique: #CBA454;

    --nav-white: #ffffff;
    --nav-text: #142536;
    --nav-muted: #6b7280;
    --nav-border: #e7ebee;
    --nav-soft: #f7f9fa;

    --nav-shadow: 0 12px 35px rgba(6, 42, 75, 0.10);
    --nav-transition: 220ms ease;

    width: 100%;
    min-width: 0;
}

.cayep-community-banner {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    margin-bottom: 18px;
    padding: 15px;
    background: #F2F8FC;
    border: 1px solid #D8E8F2;
    border-radius: 11px;
}

.cayep-community-banner-icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: var(--nav-white);
    border: 1px solid #D8E8F2;
    color: var(--cayep-nav-sky);
}

.cayep-community-banner strong {
    display: block;
    margin-bottom: 4px;
    color: var(--cayep-nav-navy);
    font-size: 13px;
    font-weight: 900;
}

.cayep-community-banner p {
    margin: 0;
    color: #596978;
    font-size: 11px;
    line-height: 1.55;
}

.cayep-community-form {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;
    padding: 14px;
    background: var(--nav-white);
    border: 1px solid var(--nav-border);
    border-radius: 11px;
    box-shadow: 0 4px 15px rgba(6, 42, 75, 0.04);
}

.cayep-community-form-avatar {
    width: 40px;
    height: 40px;
    min-width: 40px;
    overflow: hidden;
    border-radius: 50%;
    background: #EEF5FA;
}

.cayep-community-form-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.cayep-community-form-main {
    flex: 1;
    min-width: 0;
}

.cayep-community-form-main textarea,
.cayep-community-reply-form textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #D5DEE5;
    border-radius: 8px;
    outline: none;
    background: var(--nav-white);
    color: var(--nav-text);
    font-family: inherit;
    resize: vertical;
}

.cayep-community-form-main textarea {
    min-height: 88px;
    padding: 11px 12px;
    font-size: 12px;
    line-height: 1.55;
}

.cayep-community-form-main textarea:focus,
.cayep-community-reply-form textarea:focus {
    border-color: var(--cayep-nav-sky);
    box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.07);
}

.cayep-community-form-footer,
.cayep-community-reply-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
}

.cayep-community-counter {
    color: var(--nav-muted);
    font-size: 9px;
}

.cayep-community-primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 12px;
    border: 0;
    border-radius: 7px;
    background: var(--cayep-nav-navy);
    color: #fff;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 900;
    transition: var(--nav-transition);
}

.cayep-community-primary-btn:hover {
    background: var(--cayep-nav-blue);
}

.cayep-community-primary-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.cayep-community-notice {
    display: flex;
    gap: 10px;
    margin-bottom: 17px;
    padding: 12px;
    border: 1px solid #F0DE9E;
    border-radius: 9px;
    background: #FFF9E8;
}

.cayep-community-notice-icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #FFF1C2;
    color: var(--cayep-nav-orange);
}

.cayep-community-notice strong {
    display: block;
    margin-bottom: 3px;
    color: #6B4B00;
    font-size: 11px;
}

.cayep-community-notice p {
    margin: 0;
    color: #806A32;
    font-size: 10px;
    line-height: 1.55;
}

.cayep-community-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 11px;
    padding: 9px 11px;
    border: 1px solid var(--nav-border);
    border-radius: 8px;
    background: var(--nav-white);
}

.cayep-community-summary-left {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

.cayep-community-summary svg {
    color: var(--cayep-nav-sky);
}

.cayep-community-summary span {
    color: var(--nav-muted);
    font-size: 9px;
    font-weight: 800;
}

.cayep-community-summary-count {
    min-width: 21px;
    padding: 4px 6px;
    border-radius: 5px;
    background: #EEF5FA;
    color: var(--cayep-nav-sky) !important;
    text-align: center;
}

.cayep-community-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.cayep-community-thread {
    overflow: visible;
    background: var(--nav-white);
    border: 1px solid var(--nav-border);
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(6, 42, 75, 0.035);
}

.cayep-community-question {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 14px;
}

.cayep-community-avatar-button {
    width: 42px;
    height: 42px;
    min-width: 42px;
    padding: 0;
    overflow: hidden;
    border: 0;
    border-radius: 50%;
    background: #EEF5FA;
    cursor: pointer;
    transition: var(--nav-transition);
}

.cayep-community-avatar-button:hover {
    transform: scale(1.05);
    box-shadow: 0 0 0 3px rgba(15, 109, 171, .10);
}

.cayep-community-avatar-button img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.cayep-community-message {
    flex: 1;
    min-width: 0;
}

.cayep-community-author {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
}

.cayep-community-name-button {
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--cayep-nav-navy);
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 900;
}

.cayep-community-name-button:hover {
    color: var(--cayep-nav-sky);
    text-decoration: underline;
}

.cayep-community-role {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    border-radius: 5px;
    background: #EEF5FA;
    color: var(--cayep-nav-sky);
    font-size: 8px;
    font-weight: 900;
}

.cayep-community-role.management {
    background: #FFF1D8;
    color: var(--cayep-nav-orange);
}

.cayep-community-time {
    color: #8A96A0;
    font-size: 9px;
}

.cayep-community-bubble {
    position: relative;
    padding: 12px 14px;
    border: 1px solid #E5EAEF;
    border-radius: 10px;
    background: var(--nav-soft);
}

.cayep-community-bubble p {
    margin: 0;
    color: #394B5A;
    font-size: 12px;
    line-height: 1.65;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.cayep-community-action-bar {
    display: flex;
    align-items: center;
    gap: 13px;
    flex-wrap: wrap;
    margin-top: 7px;
}

.cayep-community-action {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 0;
    border: 0;
    background: transparent;
    color: #687783;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
}

.cayep-community-action:hover {
    color: var(--cayep-nav-sky);
}

.cayep-community-action.active {
    color: var(--cayep-nav-sky);
}

.cayep-community-reaction-zone {
    position: relative;
    margin: 0 14px;
    padding: 8px 0;
    border-top: 1px solid #EEF1F3;
    border-bottom: 1px solid #EEF1F3;
}

.cayep-community-reaction-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.cayep-community-reaction-left {
    display: flex;
    align-items: center;
    min-width: 0;
}

.cayep-community-reaction-icons {
    display: flex;
    align-items: center;
    margin-right: 6px;
}

.cayep-reaction-dot {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: -3px;
    border: 2px solid #fff;
    border-radius: 50%;
    background: var(--cayep-nav-sky);
    color: #fff;
    font-size: 8px;
}

.cayep-reaction-dot:first-child {
    margin-left: 0;
}

.cayep-reaction-dot.love {
    background: #D6455D;
}

.cayep-reaction-dot.helpful {
    background: var(--cayep-nav-green);
}

.cayep-reaction-dot.celebrate {
    background: var(--cayep-nav-gold);
    color: var(--cayep-nav-navy);
}

.cayep-community-reaction-count {
    color: var(--nav-muted);
    font-size: 9px;
    font-weight: 700;
}

.cayep-community-reaction-menu {
    position: absolute;
    left: 0;
    bottom: calc(100% + 5px);
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px;
    border: 1px solid var(--nav-border);
    border-radius: 9px;
    background: #fff;
    box-shadow: var(--nav-shadow);
    animation: cayepReactionPop 150ms ease;
}

@keyframes cayepReactionPop {
    from {
        opacity: 0;
        transform: translateY(5px) scale(.97);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.cayep-reaction-option {
    position: relative;
    width: 31px;
    height: 31px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: #F5F7F8;
    color: var(--cayep-nav-sky);
    cursor: pointer;
    font-size: 12px;
    transition: transform 150ms ease;
}

.cayep-reaction-option:hover {
    transform: scale(1.18);
}

.cayep-reaction-option.love {
    color: #D6455D;
}

.cayep-reaction-option.helpful {
    color: var(--cayep-nav-green);
}

.cayep-reaction-option.celebrate {
    color: var(--cayep-nav-orange);
}

.cayep-community-conversation {
    margin: 0 14px 14px 66px;
    padding: 11px;
    border: 1px solid var(--nav-border);
    border-radius: 10px;
    background: var(--nav-soft);
}

.cayep-community-conversation-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.cayep-community-conversation-header strong {
    color: var(--cayep-nav-navy);
    font-size: 10px;
    font-weight: 900;
}

.cayep-community-conversation-header span {
    color: var(--nav-muted);
    font-size: 9px;
}

.cayep-community-replies {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cayep-community-reply {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 9px;
    border: 1px solid #E4EAEF;
    border-radius: 8px;
    background: #fff;
}

.cayep-community-reply.management {
    border-left: 3px solid var(--cayep-nav-orange);
}

.cayep-community-reply.learner {
    border-left: 3px solid var(--cayep-nav-sky);
}

.cayep-community-reply .cayep-community-avatar-button {
    width: 34px;
    height: 34px;
    min-width: 34px;
}

.cayep-community-reply-content {
    flex: 1;
    min-width: 0;
}

.cayep-community-reply-bubble {
    padding: 9px 10px;
    border: 1px solid #E7EBEE;
    border-radius: 8px;
    background: #fff;
}

.cayep-community-reply-bubble p {
    margin: 0;
    color: #4D5D6B;
    font-size: 11px;
    line-height: 1.6;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.cayep-community-reply-reactions {
    margin-top: 3px;
    padding: 0;
    border: 0;
}

.cayep-community-reply-form {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 9px;
    padding: 9px;
    border: 1px solid var(--nav-border);
    border-radius: 8px;
    background: #fff;
}

.cayep-community-reply-form-icon {
    width: 31px;
    height: 31px;
    min-width: 31px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #EEF5FA;
    color: var(--cayep-nav-sky);
}

.cayep-community-reply-form-main {
    flex: 1;
    min-width: 0;
}

.cayep-community-reply-form textarea {
    min-height: 65px;
    padding: 8px 9px;
    font-size: 11px;
    line-height: 1.5;
}

.cayep-community-show-more {
    display: flex;
    justify-content: center;
    margin-top: 8px;
}

.cayep-community-show-more button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 9px;
    border: 1px solid #D8E2E9;
    border-radius: 6px;
    background: #fff;
    color: var(--cayep-nav-sky);
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
}

.cayep-community-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    min-height: 170px;
    padding: 25px;
    border: 1px dashed #D8E2E9;
    border-radius: 11px;
    background: #fff;
    text-align: center;
}

.cayep-community-empty svg {
    margin-bottom: 9px;
    color: var(--cayep-nav-sky);
    font-size: 25px;
}

.cayep-community-empty h4 {
    margin: 0 0 5px;
    color: var(--cayep-nav-navy);
    font-size: 13px;
}

.cayep-community-empty p {
    max-width: 480px;
    margin: 0;
    color: var(--nav-muted);
    font-size: 10px;
    line-height: 1.6;
}

@media (max-width: 700px) {
    .cayep-community-conversation {
        margin-left: 14px;
    }
}

@media (max-width: 480px) {
    .cayep-community-form {
        flex-direction: column;
    }

    .cayep-community-form-footer,
    .cayep-community-reply-footer {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-community-primary-btn {
        width: 100%;
    }

    .cayep-community-question {
        padding: 11px;
    }

    .cayep-community-avatar-button {
        width: 35px;
        height: 35px;
        min-width: 35px;
    }

    .cayep-community-conversation {
        margin: 0 11px 11px;
    }

    .cayep-community-reaction-menu {
        left: auto;
        right: 0;
    }
}
`;

const QuestionAndAnswer = ({
    course,
    classroom,
    setClassroom,
    isStudent,
    isManagement,
    videoType = "introduction",
    videoSection = null,
    videoContextLabel = "Introduction Video",
    loadClassroom,
}) => {
    const [questionText, setQuestionText] = useState("");
    const [replyTexts, setReplyTexts] = useState({});
    const [expandedQuestions, setExpandedQuestions] =
        useState({});
    const [visibleReplyCounts, setVisibleReplyCounts] =
        useState({});
    const [reactionMenus, setReactionMenus] =
        useState({});
    const [savingQuestion, setSavingQuestion] =
        useState(false);
    const [savingReplyId, setSavingReplyId] =
        useState(null);
    const [reactionSavingKey, setReactionSavingKey] =
        useState(null);
    const [communityDenied, setCommunityDenied] =
        useState(false);
    const [selectedProfile, setSelectedProfile] =
        useState(null);

    const mountedRef = useRef(true);

    const courseId = course?.id;

    const isSectionVideo =
        videoType === "section" &&
        Boolean(
            videoSection?.id ||
                videoSection?.section_id ||
                videoSection?.sectionId
        );

    const sectionId = isSectionVideo
        ? videoSection?.id ||
          videoSection?.section_id ||
          videoSection?.sectionId ||
          null
        : null;

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        setQuestionText("");
        setReplyTexts({});
        setExpandedQuestions({});
        setVisibleReplyCounts({});
        setReactionMenus({});
        setSelectedProfile(null);
        setCommunityDenied(false);
    }, [courseId, sectionId, videoType]);

    const questions = useMemo(() => {
        return Array.isArray(classroom?.questions)
            ? classroom.questions.filter(Boolean)
            : [];
    }, [classroom?.questions]);

    const updateQuestions = useCallback(
        (updater) => {
            setClassroom((previous) => {
                const oldQuestions = Array.isArray(
                    previous?.questions
                )
                    ? previous.questions
                    : [];

                const nextQuestions =
                    typeof updater === "function"
                        ? updater(oldQuestions)
                        : updater;

                return {
                    ...previous,
                    questions: Array.isArray(
                        nextQuestions
                    )
                        ? nextQuestions
                        : oldQuestions,
                };
            });
        },
        [setClassroom]
    );

    const getQuestionReplies = useCallback(
        (question) => {
            if (Array.isArray(question?.replies)) {
                return question.replies;
            }

            if (
                Array.isArray(
                    question?.question_replies
                )
            ) {
                return question.question_replies;
            }

            if (Array.isArray(question?.answers)) {
                return question.answers;
            }

            return [];
        },
        []
    );

    const getQuestionAuthor = useCallback(
        (question) =>
            question?.student_name ||
            question?.student?.name ||
            question?.student?.username ||
            question?.student_username ||
            "Learner",
        []
    );

    const getQuestionAvatar = useCallback(
        (question) =>
            question?.student_avatar ||
            question?.student?.avatar ||
            question?.student_image ||
            defaultAvatar,
        []
    );

    const getReplyAuthor = useCallback(
        (reply) =>
            reply?.author_name ||
            reply?.author?.name ||
            reply?.author?.username ||
            reply?.student_name ||
            "Learner",
        []
    );

    const getReplyAvatar = useCallback(
        (reply) =>
            reply?.author_avatar ||
            reply?.author?.avatar ||
            reply?.student_avatar ||
            defaultAvatar,
        []
    );

    const normalizeRole = useCallback(
        (value) =>
            String(value || "learner")
                .trim()
                .toLowerCase(),
        []
    );

    const isManagementRole = useCallback(
        (value) =>
            MANAGEMENT_ROLES.has(
                normalizeRole(value)
            ),
        [normalizeRole]
    );

    const getQuestionRole = useCallback(
        (question) =>
            question?.student_role ||
            question?.student?.role ||
            question?.student?.user_type ||
            "Learner",
        []
    );

    const getReplyRole = useCallback(
        (reply) =>
            reply?.author_role ||
            reply?.author?.role ||
            reply?.author?.user_type ||
            reply?.student_role ||
            "Learner",
        []
    );

    const getRelativeTime = useCallback(
        (item) => {
            if (item?.relative_time) {
                return item.relative_time;
            }

            if (item?.created_at) {
                const date = new Date(item.created_at);

                if (!Number.isNaN(date.getTime())) {
                    const seconds = Math.max(
                        0,
                        Math.floor(
                            (Date.now() -
                                date.getTime()) /
                                1000
                        )
                    );

                    if (seconds < 60) {
                        return "Just now";
                    }

                    const minutes = Math.floor(
                        seconds / 60
                    );

                    if (minutes < 60) {
                        return `${minutes} min${
                            minutes === 1 ? "" : "s"
                        } ago`;
                    }

                    const hours = Math.floor(
                        minutes / 60
                    );

                    if (hours < 24) {
                        return `${hours} hour${
                            hours === 1 ? "" : "s"
                        } ago`;
                    }

                    const days = Math.floor(
                        hours / 24
                    );

                    if (days < 30) {
                        return `${days} day${
                            days === 1 ? "" : "s"
                        } ago`;
                    }

                    const months = Math.floor(
                        days / 30
                    );

                    if (months < 12) {
                        return `${months} month${
                            months === 1 ? "" : "s"
                        } ago`;
                    }

                    const years = Math.floor(
                        days / 365
                    );

                    return `${years} year${
                        years === 1 ? "" : "s"
                    } ago`;
                }
            }

            return "";
        },
        []
    );

    const getProfileId = useCallback(
        (item, type) => {
            if (type === "question") {
                return (
                    item?.student_id ||
                    item?.student?.id ||
                    item?.student
                );
            }

            return (
                item?.author_id ||
                item?.author?.id ||
                item?.student_id ||
                item?.student?.id
            );
        },
        []
    );

    const openProfile = useCallback(
        (item, type) => {
            const userId = getProfileId(item, type);

            if (!userId) {
                Swal.fire({
                    icon: "info",
                    title: "Profile Unavailable",
                    text:
                        "This community member does not have a profile identifier available.",
                    confirmButtonColor:
                        "#062A4B",
                });

                return;
            }

            const profile =
                type === "question"
                    ? {
                          id: userId,
                          name: getQuestionAuthor(
                              item
                          ),
                          username:
                              item?.student_username ||
                              item?.student?.username ||
                              "",
                          email:
                              item?.student_email ||
                              item?.student?.email ||
                              "",
                          role:
                              item?.student_role ||
                              item?.student?.role ||
                              item?.student?.user_type ||
                              "Learner",
                          avatar:
                              getQuestionAvatar(item),
                      }
                    : {
                          id: userId,
                          name: getReplyAuthor(item),
                          username:
                              item?.author_username ||
                              item?.author?.username ||
                              "",
                          email:
                              item?.author_email ||
                              item?.author?.email ||
                              "",
                          role: getReplyRole(item),
                          avatar:
                              getReplyAvatar(item),
                      };

            setReactionMenus({});
            setSelectedProfile({
                userId,
                profile,
            });
        },
        [
            getProfileId,
            getQuestionAuthor,
            getQuestionAvatar,
            getReplyAuthor,
            getReplyAvatar,
            getReplyRole,
        ]
    );

    const getErrorMessage = useCallback(
        (error, fallback) => {
            const data = error?.response?.data;

            if (typeof data === "string") {
                return data;
            }

            if (data?.detail) {
                return String(data.detail);
            }

            if (data?.message) {
                return String(data.message);
            }

            if (data?.error) {
                return String(data.error);
            }

            for (const field of [
                "question",
                "reply",
                "reaction_type",
            ]) {
                if (data?.[field]) {
                    return Array.isArray(
                        data[field]
                    )
                        ? data[field].join(" ")
                        : String(data[field]);
                }
            }

            if (
                error?.response?.status === 403
            ) {
                return "You no longer have access to this course community.";
            }

            if (
                error?.response?.status === 401
            ) {
                return "Your session has expired. Please sign in again.";
            }

            return fallback;
        },
        []
    );

    const isAccessDeniedError = useCallback(
        (error) =>
            error?.response?.status === 401 ||
            error?.response?.status === 403,
        []
    );

    const questionsPath = useCallback(() => {
        if (!courseId) {
            return "";
        }

        if (isSectionVideo && sectionId) {
            return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/`;
        }

        return `api/users/subjects/${courseId}/questions/`;
    }, [courseId, isSectionVideo, sectionId]);

    const replyPath = useCallback(
        (questionId) => {
            if (!courseId || !questionId) {
                return "";
            }

            if (isSectionVideo && sectionId) {
                return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/${questionId}/reply/`;
            }

            return `api/users/subjects/${courseId}/questions/${questionId}/reply/`;
        },
        [courseId, isSectionVideo, sectionId]
    );

    const reactionPath = useCallback(() => {
        if (!courseId) {
            return "";
        }

        return `api/users/subjects/${courseId}/community/reactions/`;
    }, [courseId]);

    const getTargetType = useCallback(
        (type) => {
            if (type === "question") {
                return isSectionVideo
                    ? "section_question"
                    : "question";
            }

            return isSectionVideo
                ? "section_question_reply"
                : "question_reply";
        },
        [isSectionVideo]
    );

    const updateReactionInItem = useCallback(
        (
            item,
            reactionType,
            userId,
            removeReaction = false
        ) => {
            const currentReaction =
                item?.current_user_reaction ||
                null;

            let reactions = Array.isArray(
                item?.reactions
            )
                ? [...item.reactions]
                : [];

            const existingIndex = reactions.findIndex(
                (reaction) =>
                    String(
                        reaction?.user
                    ) === String(userId)
            );

            if (removeReaction) {
                if (existingIndex >= 0) {
                    reactions.splice(
                        existingIndex,
                        1
                    );
                }

                return {
                    ...item,
                    reactions,
                    reaction_count: Math.max(
                        0,
                        Number(
                            item?.reaction_count || 0
                        ) - (existingIndex >= 0 ? 1 : 0)
                    ),
                    current_user_reaction: null,
                };
            }

            if (existingIndex >= 0) {
                reactions[existingIndex] = {
                    ...reactions[existingIndex],
                    reaction_type: reactionType,
                };
            } else {
                reactions.push({
                    id: `local-${Date.now()}`,
                    user: userId,
                    reaction_type: reactionType,
                });
            }

            return {
                ...item,
                reactions,
                reaction_count:
                    existingIndex >= 0
                        ? Number(
                              item?.reaction_count ||
                                  0
                          )
                        : Number(
                              item?.reaction_count ||
                                  0
                          ) + 1,
                current_user_reaction:
                    reactionType,
            };
        },
        []
    );

    const handleReaction = useCallback(
        async (item, type, reactionType) => {
            if (!item?.id || !courseId) {
                return;
            }

            const targetType =
                getTargetType(type);

            const targetKey = `${targetType}-${item.id}`;

            if (reactionSavingKey) {
                return;
            }

            setReactionSavingKey(targetKey);

            try {
                const currentReaction =
                    item?.current_user_reaction ||
                    null;

                if (
                    currentReaction ===
                    reactionType
                ) {
                    await api.delete(
                        reactionPath(),
                        {
                            data: {
                                target_type:
                                    targetType,
                                object_id: item.id,
                            },
                        }
                    );

                    updateQuestions(
                        (previous) =>
                            previous.map(
                                (question) => {
                                    if (
                                        type ===
                                            "question" &&
                                        String(
                                            question?.id
                                        ) ===
                                            String(
                                                item.id
                                            )
                                    ) {
                                        return updateReactionInItem(
                                            question,
                                            reactionType,
                                            item?.current_user ||
                                                item?.student_id ||
                                                item?.student?.id,
                                            true
                                        );
                                    }

                                    if (
                                        type ===
                                            "question_reply"
                                    ) {
                                        return {
                                            ...question,
                                            replies:
                                                getQuestionReplies(
                                                    question
                                                ).map(
                                                    (
                                                        reply
                                                    ) =>
                                                        String(
                                                            reply?.id
                                                        ) ===
                                                        String(
                                                            item.id
                                                        )
                                                            ? updateReactionInItem(
                                                                  reply,
                                                                  reactionType,
                                                                  item?.current_user ||
                                                                      item?.author_id ||
                                                                      item?.author?.id ||
                                                                      item?.student_id,
                                                                  true
                                                              )
                                                            : reply
                                                ),
                                        };
                                    }

                                    return question;
                                }
                            )
                    );
                } else {
                    await api.post(
                        reactionPath(),
                        {
                            target_type:
                                targetType,
                            object_id: item.id,
                            reaction_type:
                                reactionType,
                        }
                    );

                    updateQuestions(
                        (previous) =>
                            previous.map(
                                (question) => {
                                    if (
                                        type ===
                                            "question" &&
                                        String(
                                            question?.id
                                        ) ===
                                            String(
                                                item.id
                                            )
                                    ) {
                                        return updateReactionInItem(
                                            question,
                                            reactionType,
                                            item?.current_user ||
                                                item?.student_id ||
                                                item?.student?.id
                                        );
                                    }

                                    if (
                                        type ===
                                            "question_reply"
                                    ) {
                                        return {
                                            ...question,
                                            replies:
                                                getQuestionReplies(
                                                    question
                                                ).map(
                                                    (
                                                        reply
                                                    ) =>
                                                        String(
                                                            reply?.id
                                                        ) ===
                                                        String(
                                                            item.id
                                                        )
                                                            ? updateReactionInItem(
                                                                  reply,
                                                                  reactionType,
                                                                  item?.current_user ||
                                                                      item?.author_id ||
                                                                      item?.author?.id ||
                                                                      item?.student_id
                                                              )
                                                            : reply
                                                ),
                                        };
                                    }

                                    return question;
                                }
                            )
                    );
                }

                setReactionMenus({});
            } catch (error) {
                console.error(
                    "Community reaction failed:",
                    error
                );

                if (
                    isAccessDeniedError(error)
                ) {
                    setCommunityDenied(true);
                }

                Swal.fire({
                    icon: "error",
                    title: "Reaction Not Saved",
                    text: getErrorMessage(
                        error,
                        "Your reaction could not be saved."
                    ),
                    confirmButtonColor:
                        "#062A4B",
                });
            } finally {
                if (mountedRef.current) {
                    setReactionSavingKey(null);
                }
            }
        },
        [
            courseId,
            getTargetType,
            reactionSavingKey,
            reactionPath,
            updateQuestions,
            updateReactionInItem,
            getQuestionReplies,
            isAccessDeniedError,
            getErrorMessage,
        ]
    );

    const renderReactionMenu = useCallback(
        (item, type) => {
            const targetType =
                getTargetType(type);

            const menuKey = `${targetType}-${item?.id}`;

            if (reactionMenus[menuKey] !== true) {
                return null;
            }

            return (
                <div className="cayep-community-reaction-menu">
                    {REACTION_OPTIONS.map(
                        (reaction) => (
                            <button
                                key={
                                    reaction.type
                                }
                                type="button"
                                className={`cayep-reaction-option ${reaction.className}`}
                                title={
                                    reaction.label
                                }
                                onClick={() =>
                                    handleReaction(
                                        item,
                                        type,
                                        reaction.type
                                    )
                                }
                            >
                                {
                                    reaction.icon
                                }
                            </button>
                        )
                    )}
                </div>
            );
        },
        [
            getTargetType,
            reactionMenus,
            handleReaction,
        ]
    );

    const renderReactionSummary =
        useCallback(
            (item, type) => {
                const reactions = Array.isArray(
                    item?.reactions
                )
                    ? item.reactions
                    : [];

                const count = Number(
                    item?.reaction_count ||
                        reactions.length ||
                        0
                );

                const uniqueTypes = [
                    ...new Set(
                        reactions
                            .map(
                                (reaction) =>
                                    reaction?.reaction_type
                            )
                            .filter(Boolean)
                    ),
                ].slice(0, 3);

                const currentReaction =
                    item?.current_user_reaction;

                const menuKey = `${getTargetType(
                    type
                )}-${item?.id}`;

                return (
                    <div className="cayep-community-reaction-zone">
                        {renderReactionMenu(
                            item,
                            type
                        )}

                        <div className="cayep-community-reaction-summary">
                            <div className="cayep-community-reaction-left">
                                {uniqueTypes.length >
                                    0 && (
                                    <div className="cayep-community-reaction-icons">
                                        {uniqueTypes.map(
                                            (
                                                reactionType
                                            ) => {
                                                const reaction =
                                                    REACTION_OPTIONS.find(
                                                        (
                                                            option
                                                        ) =>
                                                            option.type ===
                                                            reactionType
                                                    );

                                                return (
                                                    <span
                                                        key={
                                                            reactionType
                                                        }
                                                        className={`cayep-reaction-dot ${reaction?.className || ""}`}
                                                    >
                                                        {
                                                            reaction?.icon
                                                        }
                                                    </span>
                                                );
                                            }
                                        )}
                                    </div>
                                )}

                                <span className="cayep-community-reaction-count">
                                    {count || "Be the first to react"}
                                </span>
                            </div>

                            <button
                                type="button"
                                className={`cayep-community-action ${
                                    currentReaction
                                        ? "active"
                                        : ""
                                }`}
                                onClick={() =>
                                    setReactionMenus(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,
                                            [menuKey]:
                                                !previous[
                                                    menuKey
                                                ],
                                        })
                                    )
                                }
                            >
                                <FaThumbsUp />
                                {currentReaction
                                    ? currentReaction
                                          .charAt(
                                              0
                                          )
                                          .toUpperCase() +
                                      currentReaction.slice(
                                          1
                                      )
                                    : "React"}
                            </button>
                        </div>
                    </div>
                );
            },
            [
                getTargetType,
                renderReactionMenu,
                reactionMenus,
            ]
        );

    const submitQuestion = useCallback(
        async (event) => {
            event.preventDefault();

            const text = questionText
                .trim()
                .slice(0, MAX_QUESTION_LENGTH);

            if (
                !isStudent ||
                communityDenied ||
                !text ||
                savingQuestion
            ) {
                return;
            }

            const endpoint = questionsPath();

            if (!endpoint) {
                return;
            }

            setSavingQuestion(true);

            try {
                const response = await api.post(
                    endpoint,
                    {
                        question: text,
                    }
                );

                const created =
                    response?.data;

                if (created) {
                    updateQuestions(
                        (previous) => [
                            created,
                            ...previous.filter(
                                (item) =>
                                    String(
                                        item?.id
                                    ) !==
                                    String(
                                        created?.id
                                    )
                            ),
                        ]
                    );
                }

                setQuestionText("");
                setCommunityDenied(false);

                Swal.fire({
                    icon: "success",
                    title: "Question Posted",
                    text:
                        "Your question has been added to the learning community.",
                    timer: 1200,
                    showConfirmButton: false,
                });

                if (
                    typeof loadClassroom ===
                    "function"
                ) {
                    await loadClassroom(false);
                }
            } catch (error) {
                console.error(
                    "Unable to post question:",
                    error
                );

                if (
                    isAccessDeniedError(error)
                ) {
                    setCommunityDenied(true);
                }

                Swal.fire({
                    icon: "error",
                    title: "Question Not Posted",
                    text: getErrorMessage(
                        error,
                        "Your question could not be posted."
                    ),
                    confirmButtonColor:
                        "#062A4B",
                });
            } finally {
                if (mountedRef.current) {
                    setSavingQuestion(false);
                }
            }
        },
        [
            questionText,
            isStudent,
            communityDenied,
            savingQuestion,
            questionsPath,
            updateQuestions,
            loadClassroom,
            isAccessDeniedError,
            getErrorMessage,
        ]
    );

    const submitReply = useCallback(
        async (event, questionId) => {
            event.preventDefault();

            const text = String(
                replyTexts[questionId] || ""
            )
                .trim()
                .slice(0, MAX_REPLY_LENGTH);

            if (
                !questionId ||
                !text ||
                savingReplyId !== null ||
                communityDenied
            ) {
                return;
            }

            if (!isStudent && !isManagement) {
                return;
            }

            const endpoint =
                replyPath(questionId);

            if (!endpoint) {
                return;
            }

            setSavingReplyId(questionId);

            try {
                const response = await api.post(
                    endpoint,
                    {
                        reply: text,
                    }
                );

                const createdReply =
                    response?.data;

                if (createdReply) {
                    updateQuestions(
                        (previous) =>
                            previous.map(
                                (question) => {
                                    if (
                                        String(
                                            question?.id
                                        ) !==
                                        String(
                                            questionId
                                        )
                                    ) {
                                        return question;
                                    }

                                    const replies =
                                        getQuestionReplies(
                                            question
                                        );

                                    return {
                                        ...question,
                                        replies: [
                                            ...replies,
                                            createdReply,
                                        ],
                                    };
                                }
                            )
                    );
                }

                setReplyTexts(
                    (previous) => ({
                        ...previous,
                        [questionId]: "",
                    })
                );

                setExpandedQuestions(
                    (previous) => ({
                        ...previous,
                        [questionId]: true,
                    })
                );

                setCommunityDenied(false);

                Swal.fire({
                    icon: "success",
                    title: "Reply Posted",
                    text:
                        "Your response has been added to the discussion.",
                    timer: 1000,
                    showConfirmButton: false,
                });

                if (
                    typeof loadClassroom ===
                    "function"
                ) {
                    await loadClassroom(false);
                }
            } catch (error) {
                console.error(
                    "Unable to post reply:",
                    error
                );

                if (
                    isAccessDeniedError(error)
                ) {
                    setCommunityDenied(true);
                }

                Swal.fire({
                    icon: "error",
                    title: "Reply Not Posted",
                    text: getErrorMessage(
                        error,
                        "Your reply could not be posted."
                    ),
                    confirmButtonColor:
                        "#062A4B",
                });
            } finally {
                if (mountedRef.current) {
                    setSavingReplyId(null);
                }
            }
        },
        [
            replyTexts,
            savingReplyId,
            communityDenied,
            isStudent,
            isManagement,
            replyPath,
            updateQuestions,
            getQuestionReplies,
            loadClassroom,
            isAccessDeniedError,
            getErrorMessage,
        ]
    );

    const toggleQuestion = useCallback(
        (questionId) => {
            setExpandedQuestions(
                (previous) => ({
                    ...previous,
                    [questionId]:
                        !previous[questionId],
                })
            );
        },
        []
    );

    const showMoreReplies = useCallback(
        (questionId, total) => {
            setVisibleReplyCounts(
                (previous) => ({
                    ...previous,
                    [questionId]: Math.min(
                        total,
                        (previous[
                            questionId
                        ] ||
                            INITIAL_REPLIES_VISIBLE) +
                            REPLIES_LOAD_STEP
                    ),
                })
            );
        },
        []
    );

    const collapseReplies = useCallback(
        (questionId) => {
            setVisibleReplyCounts(
                (previous) => ({
                    ...previous,
                    [questionId]:
                        INITIAL_REPLIES_VISIBLE,
                })
            );
        },
        []
    );

    const totalReplies = useMemo(
        () =>
            questions.reduce(
                (total, question) =>
                    total +
                    getQuestionReplies(
                        question
                    ).length,
                0
            ),
        [questions, getQuestionReplies]
    );

    const totalReactions = useMemo(
        () =>
            questions.reduce(
                (total, question) =>
                    total +
                    Number(
                        question?.reaction_count ||
                            0
                    ) +
                    getQuestionReplies(
                        question
                    ).reduce(
                        (
                            replyTotal,
                            reply
                        ) =>
                            replyTotal +
                            Number(
                                reply?.reaction_count ||
                                    0
                            ),
                        0
                    ),
                0
            ),
        [questions, getQuestionReplies]
    );

    if (selectedProfile) {
        return (
            <>
                <style>
                    {QUESTION_AND_ANSWER_STYLES}
                </style>

                <StudentPersonalBio
                    userId={
                        selectedProfile.userId
                    }
                    course={course}
                    fallbackProfile={
                        selectedProfile.profile
                    }
                    onBack={() =>
                        setSelectedProfile(null)
                    }
                />
            </>
        );
    }

    return (
        <>
            <style>
                {QUESTION_AND_ANSWER_STYLES}
            </style>

            <section className="cayep-community-root">
                <div className="cayep-classroom-section-heading">
                    <div className="cayep-classroom-heading-icon">
                        <FaQuestionCircle />
                    </div>

                    <div>
                        <span className="cayep-classroom-section-kicker">
                            LEARNING COMMUNITY
                        </span>

                        <h3>
                            Questions
                            &amp;
                            Answers
                        </h3>

                        <p>
                            Connect with fellow
                            learners and
                            facilitators, ask
                            questions, share
                            perspectives, react to
                            useful contributions,
                            and continue the
                            conversation around
                            your course.
                        </p>

                        <div className="cayep-classroom-context-pill">
                            <FaBookOpen />
                            {videoContextLabel}
                        </div>
                    </div>
                </div>

                {communityDenied && (
                    <div className="cayep-community-notice">
                        <div className="cayep-community-notice-icon">
                            <FaUsers />
                        </div>

                        <div>
                            <strong>
                                Community Access
                                Unavailable
                            </strong>

                            <p>
                                Your access to this
                                course community
                                has changed. Please
                                refresh the classroom
                                or contact the course
                                facilitator.
                            </p>
                        </div>
                    </div>
                )}

                {!communityDenied && (
                    <>
                        <div className="cayep-community-banner">
                            <div className="cayep-community-banner-icon">
                                <FaUsers />
                            </div>

                            <div>
                                <strong>
                                    Course Learning
                                    Community
                                </strong>

                                <p>
                                    Ask questions,
                                    reply to
                                    discussions,
                                    react to useful
                                    contributions,
                                    and connect with
                                    other people
                                    participating in
                                    this course.
                                </p>
                            </div>
                        </div>

                        {isStudent && (
                            <form
                                className="cayep-community-form"
                                onSubmit={
                                    submitQuestion
                                }
                            >
                                <div className="cayep-community-form-avatar">
                                    <img
                                        src={
                                            defaultAvatar
                                        }
                                        alt=""
                                    />
                                </div>

                                <div className="cayep-community-form-main">
                                    <textarea
                                        value={
                                            questionText
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setQuestionText(
                                                event
                                                    .target
                                                    .value
                                                    .slice(
                                                        0,
                                                        MAX_QUESTION_LENGTH
                                                    )
                                            )
                                        }
                                        placeholder="Ask a question or share an idea with the course community..."
                                        maxLength={
                                            MAX_QUESTION_LENGTH
                                        }
                                        disabled={
                                            savingQuestion
                                        }
                                    />

                                    <div className="cayep-community-form-footer">
                                        <span className="cayep-community-counter">
                                            {
                                                questionText.length
                                            }
                                            /
                                            {
                                                MAX_QUESTION_LENGTH
                                            }
                                        </span>

                                        <button
                                            type="submit"
                                            className="cayep-community-primary-btn"
                                            disabled={
                                                savingQuestion ||
                                                !questionText.trim()
                                            }
                                        >
                                            <FaPaperPlane />

                                            {savingQuestion
                                                ? "Posting..."
                                                : "Post Question"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}

                        {isManagement && (
                            <div className="cayep-community-notice">
                                <div className="cayep-community-notice-icon">
                                    <FaChalkboardTeacher />
                                </div>

                                <div>
                                    <strong>
                                        Facilitation
                                        Community
                                    </strong>

                                    <p>
                                        Respond to
                                        learner
                                        questions,
                                        clarify
                                        concepts, and
                                        contribute
                                        professional
                                        guidance.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="cayep-community-summary">
                            <div className="cayep-community-summary-left">
                                <FaComment />

                                <span>
                                    Discussions
                                </span>

                                <span className="cayep-community-summary-count">
                                    {
                                        questions.length
                                    }
                                </span>
                            </div>

                            <span>
                                {totalReplies}{" "}
                                {totalReplies ===
                                1
                                    ? "reply"
                                    : "replies"}
                                {" • "}
                                {totalReactions}{" "}
                                reactions
                            </span>
                        </div>

                        {questions.length ===
                        0 ? (
                            <div className="cayep-community-empty">
                                <FaUsers />

                                <h4>
                                    Start the
                                    Conversation
                                </h4>

                                <p>
                                    Questions and
                                    responses about
                                    this course
                                    content will
                                    appear here.
                                    Start the
                                    conversation by
                                    asking a useful
                                    question.
                                </p>
                            </div>
                        ) : (
                            <div className="cayep-community-list">
                                {questions.map(
                                    (
                                        question,
                                        questionIndex
                                    ) => {
                                        const questionKey =
                                            question?.id ||
                                            `question-${questionIndex}`;

                                        const replies =
                                            getQuestionReplies(
                                                question
                                            );

                                        const expanded =
                                            Boolean(
                                                expandedQuestions[
                                                    questionKey
                                                ]
                                            );

                                        const visibleCount =
                                            Math.min(
                                                replies.length,
                                                visibleReplyCounts[
                                                    questionKey
                                                ] ||
                                                    INITIAL_REPLIES_VISIBLE
                                            );

                                        const visibleReplies =
                                            replies.slice(
                                                0,
                                                visibleCount
                                            );

                                        const hasMore =
                                            replies.length >
                                            visibleCount;

                                        const questionRole =
                                            getQuestionRole(
                                                question
                                            );

                                        const questionIsManagement =
                                            isManagementRole(
                                                questionRole
                                            );

                                        return (
                                            <article
                                                className="cayep-community-thread"
                                                key={
                                                    questionKey
                                                }
                                            >
                                                <div className="cayep-community-question">
                                                    <button
                                                        type="button"
                                                        className="cayep-community-avatar-button"
                                                        onClick={() =>
                                                            openProfile(
                                                                question,
                                                                "question"
                                                            )
                                                        }
                                                    >
                                                        <img
                                                            src={getQuestionAvatar(
                                                                question
                                                            )}
                                                            alt=""
                                                            onError={(
                                                                event
                                                            ) => {
                                                                event.currentTarget.src =
                                                                    defaultAvatar;
                                                            }}
                                                        />
                                                    </button>

                                                    <div className="cayep-community-message">
                                                        <div className="cayep-community-author">
                                                            <button
                                                                type="button"
                                                                className="cayep-community-name-button"
                                                                onClick={() =>
                                                                    openProfile(
                                                                        question,
                                                                        "question"
                                                                    )
                                                                }
                                                            >
                                                                {getQuestionAuthor(
                                                                    question
                                                                )}
                                                            </button>

                                                            <span
                                                                className={`cayep-community-role ${
                                                                    questionIsManagement
                                                                        ? "management"
                                                                        : ""
                                                                }`}
                                                            >
                                                                {questionIsManagement ? (
                                                                    <FaChalkboardTeacher />
                                                                ) : (
                                                                    <FaUser />
                                                                )}

                                                                {questionIsManagement
                                                                    ? "Facilitator"
                                                                    : "Learner"}
                                                            </span>

                                                            <span className="cayep-community-time">
                                                                {getRelativeTime(
                                                                    question
                                                                )}
                                                            </span>
                                                        </div>

                                                        <div className="cayep-community-bubble">
                                                            <p>
                                                                {
                                                                    question?.question
                                                                }
                                                            </p>
                                                        </div>

                                                        {renderReactionSummary(
                                                            question,
                                                            "question"
                                                        )}

                                                        <div className="cayep-community-action-bar">
                                                            <button
                                                                type="button"
                                                                className="cayep-community-action"
                                                                onClick={() =>
                                                                    toggleQuestion(
                                                                        questionKey
                                                                    )
                                                                }
                                                            >
                                                                {expanded ? (
                                                                    <FaChevronUp />
                                                                ) : (
                                                                    <FaReply />
                                                                )}

                                                                {expanded
                                                                    ? "Hide Discussion"
                                                                    : `Comment${
                                                                          replies.length
                                                                              ? ` (${replies.length})`
                                                                              : ""
                                                                      }`}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="cayep-community-action"
                                                                onClick={() =>
                                                                    setReactionMenus(
                                                                        (
                                                                            previous
                                                                        ) => ({
                                                                            ...previous,
                                                                            [`${getTargetType(
                                                                                "question"
                                                                            )}-${question?.id}`]:
                                                                                true,
                                                                        })
                                                                    )
                                                                }
                                                            >
                                                                <FaThumbsUp />
                                                                React
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {expanded && (
                                                    <div className="cayep-community-conversation">
                                                        <div className="cayep-community-conversation-header">
                                                            <strong>
                                                                Discussion
                                                            </strong>

                                                            <span>
                                                                {
                                                                    replies.length
                                                                }{" "}
                                                                {replies.length ===
                                                                1
                                                                    ? "reply"
                                                                    : "replies"}
                                                            </span>
                                                        </div>

                                                        {visibleReplies.length >
                                                        0 ? (
                                                            <div className="cayep-community-replies">
                                                                {visibleReplies.map(
                                                                    (
                                                                        reply,
                                                                        replyIndex
                                                                    ) => {
                                                                        const replyRole =
                                                                            getReplyRole(
                                                                                reply
                                                                            );

                                                                        const replyManagement =
                                                                            isManagementRole(
                                                                                replyRole
                                                                            );

                                                                        const replyKey =
                                                                            reply?.id ||
                                                                            `${questionKey}-reply-${replyIndex}`;

                                                                        return (
                                                                            <div
                                                                                key={
                                                                                    replyKey
                                                                                }
                                                                                className={`cayep-community-reply ${
                                                                                    replyManagement
                                                                                        ? "management"
                                                                                        : "learner"
                                                                                }`}
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    className="cayep-community-avatar-button"
                                                                                    onClick={() =>
                                                                                        openProfile(
                                                                                            reply,
                                                                                            "reply"
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <img
                                                                                        src={getReplyAvatar(
                                                                                            reply
                                                                                        )}
                                                                                        alt=""
                                                                                        onError={(
                                                                                            event
                                                                                        ) => {
                                                                                            event.currentTarget.src =
                                                                                                defaultAvatar;
                                                                                        }}
                                                                                    />
                                                                                </button>

                                                                                <div className="cayep-community-reply-content">
                                                                                    <div className="cayep-community-author">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="cayep-community-name-button"
                                                                                            onClick={() =>
                                                                                                openProfile(
                                                                                                    reply,
                                                                                                    "reply"
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            {getReplyAuthor(
                                                                                                reply
                                                                                            )}
                                                                                        </button>

                                                                                        <span
                                                                                            className={`cayep-community-role ${
                                                                                                replyManagement
                                                                                                    ? "management"
                                                                                                    : ""
                                                                                            }`}
                                                                                        >
                                                                                            {replyManagement ? (
                                                                                                <FaChalkboardTeacher />
                                                                                            ) : (
                                                                                                <FaUser />
                                                                                            )}

                                                                                            {replyManagement
                                                                                                ? "Facilitator"
                                                                                                : "Learner"}
                                                                                        </span>

                                                                                        <span className="cayep-community-time">
                                                                                            {getRelativeTime(
                                                                                                reply
                                                                                            )}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="cayep-community-reply-bubble">
                                                                                        <p>
                                                                                            {
                                                                                                reply?.reply
                                                                                            }
                                                                                        </p>
                                                                                    </div>

                                                                                    <div className="cayep-community-reply-reactions">
                                                                                        {renderReactionSummary(
                                                                                            reply,
                                                                                            "question_reply"
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="cayep-community-empty">
                                                                <FaComment />

                                                                <h4>
                                                                    No Replies
                                                                    Yet
                                                                </h4>

                                                                <p>
                                                                    Be the
                                                                    first
                                                                    person
                                                                    to
                                                                    respond
                                                                    to this
                                                                    discussion.
                                                                </p>
                                                            </div>
                                                        )}

                                                        {hasMore && (
                                                            <div className="cayep-community-show-more">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        showMoreReplies(
                                                                            questionKey,
                                                                            replies.length
                                                                        )
                                                                    }
                                                                >
                                                                    <FaChevronDown />

                                                                    Show{" "}
                                                                    {Math.min(
                                                                        REPLIES_LOAD_STEP,
                                                                        replies.length -
                                                                            visibleCount
                                                                    )}{" "}
                                                                    more
                                                                </button>
                                                            </div>
                                                        )}

                                                        {!hasMore &&
                                                            replies.length >
                                                                INITIAL_REPLIES_VISIBLE && (
                                                                <div className="cayep-community-show-more">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            collapseReplies(
                                                                                questionKey
                                                                            )
                                                                        }
                                                                    >
                                                                        <FaChevronUp />
                                                                        Show
                                                                        fewer
                                                                    </button>
                                                                </div>
                                                            )}

                                                        {(isStudent ||
                                                            isManagement) && (
                                                            <form
                                                                className="cayep-community-reply-form"
                                                                onSubmit={(
                                                                    event
                                                                ) =>
                                                                    submitReply(
                                                                        event,
                                                                        question?.id
                                                                    )
                                                                }
                                                            >
                                                                <div className="cayep-community-reply-form-icon">
                                                                    {isManagement ? (
                                                                        <FaChalkboardTeacher />
                                                                    ) : (
                                                                        <FaUser />
                                                                    )}
                                                                </div>

                                                                <div className="cayep-community-reply-form-main">
                                                                    <textarea
                                                                        value={
                                                                            replyTexts[
                                                                                question?.id
                                                                            ] ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            setReplyTexts(
                                                                                (
                                                                                    previous
                                                                                ) => ({
                                                                                    ...previous,
                                                                                    [question?.id]:
                                                                                        event.target.value.slice(
                                                                                            0,
                                                                                            MAX_REPLY_LENGTH
                                                                                        ),
                                                                                })
                                                                            )
                                                                        }
                                                                        placeholder={
                                                                            isManagement
                                                                                ? "Write a professional response or guidance..."
                                                                                : "Write a reply to this discussion..."
                                                                        }
                                                                        maxLength={
                                                                            MAX_REPLY_LENGTH
                                                                        }
                                                                    />

                                                                    <div className="cayep-community-reply-footer">
                                                                        <span className="cayep-community-counter">
                                                                            {
                                                                                String(
                                                                                    replyTexts[
                                                                                        question?.id
                                                                                    ] ||
                                                                                        ""
                                                                                ).length
                                                                            }
                                                                            /
                                                                            {
                                                                                MAX_REPLY_LENGTH
                                                                            }
                                                                        </span>

                                                                        <button
                                                                            type="submit"
                                                                            className="cayep-community-primary-btn"
                                                                            disabled={
                                                                                savingReplyId ===
                                                                                    question?.id ||
                                                                                !String(
                                                                                    replyTexts[
                                                                                        question?.id
                                                                                    ] ||
                                                                                        ""
                                                                                ).trim()
                                                                            }
                                                                        >
                                                                            <FaPaperPlane />

                                                                            {savingReplyId ===
                                                                            question?.id
                                                                                ? "Sending..."
                                                                                : "Reply"}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </form>
                                                        )}
                                                    </div>
                                                )}
                                            </article>
                                        );
                                    }
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>
        </>
    );
};

export default QuestionAndAnswer;





// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useRef,
//     useState,
// } from "react";

// import {
//     FaBookOpen,
//     FaChalkboardTeacher,
//     FaChevronDown,
//     FaChevronUp,
//     FaComment,
//     FaPaperPlane,
//     FaQuestionCircle,
//     FaReply,
//     FaUser,
//     FaUsers,
// } from "react-icons/fa";

// import Swal from "sweetalert2";

// import api from "../api/api";
// import defaultAvatar from "./learningenvironment.jpg";

// const MANAGEMENT_ROLES = new Set([
//     "teacher",
//     "facilitator",
//     "instructor",
//     "admin",
//     "administrator",
//     "principal",
//     "proprietor",
// ]);

// const MAX_QUESTION_LENGTH = 3000;
// const MAX_REPLY_LENGTH = 3000;
// const INITIAL_REPLIES_VISIBLE = 5;
// const REPLIES_LOAD_STEP = 5;

// const QUESTION_AND_ANSWER_STYLES = `
// .cayep-community-root {
//     width: 100%;
//     min-width: 0;
// }

// .cayep-classroom-community-banner {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     margin-bottom: 18px;
//     padding: 15px;
//     background: #F2F8FC;
//     border: 1px solid #D8E8F2;
//     border-radius: 11px;
// }

// .cayep-classroom-community-icon {
//     width: 38px;
//     height: 38px;
//     min-width: 38px;
//     border-radius: 10px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #FFFFFF;
//     color: #0F6DAB;
//     border: 1px solid #D8E8F2;
//     font-size: 16px;
// }

// .cayep-classroom-community-banner strong {
//     display: block;
//     margin-bottom: 4px;
//     color: #062A4B;
//     font-size: 13px;
//     font-weight: 800;
// }

// .cayep-classroom-community-banner p {
//     margin: 0;
//     color: #596978;
//     font-size: 11px;
//     line-height: 1.55;
// }

// .cayep-community-access-notice {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     margin-bottom: 18px;
//     padding: 14px 15px;
//     background: #FFF9E8;
//     border: 1px solid #F0DE9E;
//     border-radius: 10px;
// }

// .cayep-community-access-notice-icon {
//     width: 36px;
//     height: 36px;
//     min-width: 36px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 9px;
//     background: #FFF1C2;
//     color: #C0750D;
// }

// .cayep-community-access-notice strong {
//     display: block;
//     margin-bottom: 3px;
//     color: #6B4B00;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-community-access-notice p {
//     margin: 0;
//     color: #806A32;
//     font-size: 11px;
//     line-height: 1.55;
// }

// .cayep-classroom-question-form {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     margin-bottom: 20px;
//     padding: 15px;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 11px;
// }

// .cayep-classroom-form-avatar {
//     width: 38px;
//     height: 38px;
//     min-width: 38px;
//     border-radius: 50%;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 14px;
// }

// .cayep-classroom-form-main {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-classroom-form-main label {
//     display: block;
//     margin-bottom: 7px;
//     color: #062A4B;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-classroom-form-main textarea {
//     display: block;
//     width: 100%;
//     min-height: 100px;
//     box-sizing: border-box;
//     padding: 11px 12px;
//     border: 1px solid #D5DEE5;
//     border-radius: 8px;
//     outline: none;
//     resize: vertical;
//     background: #FFFFFF;
//     color: #142536;
//     font-family: inherit;
//     font-size: 12px;
//     line-height: 1.55;
// }

// .cayep-classroom-form-main textarea:focus {
//     border-color: #0F6DAB;
//     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
// }

// .cayep-classroom-form-main textarea:disabled {
//     background: #F7F9FB;
//     cursor: not-allowed;
// }

// .cayep-classroom-form-footer {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 10px;
//     margin-top: 8px;
// }

// .cayep-classroom-form-footer > span {
//     color: #7A8793;
//     font-size: 10px;
// }

// .cayep-classroom-form-footer button,
// .cayep-community-reply-form-footer button {
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     gap: 7px;
//     border: 0;
//     border-radius: 8px;
//     padding: 9px 13px;
//     background: #062A4B;
//     color: #FFFFFF;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 11px;
//     font-weight: 800;
//     transition:
//         background 0.18s ease,
//         transform 0.18s ease,
//         opacity 0.18s ease;
// }

// .cayep-classroom-form-footer button:hover,
// .cayep-community-reply-form-footer button:hover {
//     background: #063A6B;
// }

// .cayep-classroom-form-footer button:active,
// .cayep-community-reply-form-footer button:active {
//     transform: translateY(1px);
// }

// .cayep-classroom-form-footer button:disabled,
// .cayep-community-reply-form-footer button:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
//     transform: none;
// }

// .cayep-classroom-teacher-notice {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     margin-bottom: 18px;
//     padding: 13px 15px;
//     background: #FFF9E8;
//     border: 1px solid #F0DE9E;
//     border-radius: 10px;
// }

// .cayep-classroom-teacher-notice-icon {
//     width: 36px;
//     height: 36px;
//     min-width: 36px;
//     border-radius: 9px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #FFF1C2;
//     color: #C0750D;
// }

// .cayep-classroom-teacher-notice strong {
//     display: block;
//     margin-bottom: 3px;
//     color: #6B4B00;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-classroom-teacher-notice p {
//     margin: 0;
//     color: #806A32;
//     font-size: 11px;
//     line-height: 1.55;
// }

// .cayep-community-summary {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 12px;
//     margin-bottom: 12px;
//     padding: 9px 11px;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 9px;
// }

// .cayep-community-summary-left {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     min-width: 0;
// }

// .cayep-community-summary-icon {
//     color: #0F6DAB;
//     font-size: 12px;
// }

// .cayep-community-summary-text {
//     color: #596978;
//     font-size: 10px;
//     font-weight: 700;
// }

// .cayep-community-summary-count {
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     min-width: 22px;
//     height: 22px;
//     padding: 0 6px;
//     border-radius: 7px;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 9px;
//     font-weight: 800;
// }

// .cayep-classroom-question-list {
//     display: flex;
//     flex-direction: column;
//     gap: 12px;
// }

// .cayep-community-thread {
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 12px;
//     overflow: hidden;
// }

// .cayep-community-question-row {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     padding: 15px;
// }

// .cayep-community-avatar,
// .cayep-community-reply-avatar {
//     width: 40px;
//     height: 40px;
//     min-width: 40px;
//     border-radius: 50%;
//     overflow: hidden;
//     background: #EEF5FA;
//     border: 1px solid #DCE7EE;
// }

// .cayep-community-avatar img,
// .cayep-community-reply-avatar img {
//     width: 100%;
//     height: 100%;
//     display: block;
//     object-fit: cover;
// }

// .cayep-community-message-area {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-community-author-line {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 7px;
//     margin-bottom: 7px;
// }

// .cayep-community-author-line strong {
//     color: #062A4B;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-community-author-role {
//     display: inline-flex;
//     align-items: center;
//     gap: 4px;
//     padding: 3px 6px;
//     border-radius: 5px;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 8px;
//     font-weight: 800;
// }

// .cayep-community-author-line span.date {
//     color: #8A96A0;
//     font-size: 9px;
// }

// .cayep-community-question-bubble {
//     position: relative;
//     padding: 12px 14px 12px 26px;
//     background: #F7F9FB;
//     border: 1px solid #E4EAEF;
//     border-radius: 10px;
// }

// .cayep-community-question-bubble p {
//     margin: 0;
//     color: #394B5A;
//     font-size: 12px;
//     line-height: 1.65;
//     white-space: pre-wrap;
//     overflow-wrap: anywhere;
//     word-break: break-word;
// }

// .cayep-community-quote-mark {
//     position: absolute;
//     top: 5px;
//     left: 9px;
//     color: #C0750D;
//     font-size: 20px;
//     font-weight: 800;
//     line-height: 1;
// }

// .cayep-community-message-actions {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 12px;
//     margin-top: 8px;
// }

// .cayep-community-message-actions button {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     padding: 5px 0;
//     border: 0;
//     background: transparent;
//     color: #0F6DAB;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 10px;
//     font-weight: 800;
// }

// .cayep-community-message-actions button:hover {
//     color: #062A4B;
// }

// .cayep-community-conversation {
//     margin: 0 15px 15px 67px;
//     padding: 12px;
//     background: #F7F9FB;
//     border: 1px solid #E0E7EC;
//     border-radius: 10px;
// }

// .cayep-community-replies-header {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 10px;
//     margin-bottom: 9px;
// }

// .cayep-community-replies-header strong {
//     color: #062A4B;
//     font-size: 10px;
//     font-weight: 800;
// }

// .cayep-community-replies-header span {
//     color: #8A96A0;
//     font-size: 9px;
// }

// .cayep-community-reply-list {
//     display: flex;
//     flex-direction: column;
// }

// .cayep-community-reply-row {
//     display: flex;
//     align-items: flex-start;
//     gap: 10px;
//     padding: 10px;
//     background: #FFFFFF;
//     border: 1px solid #E4EAEF;
//     border-radius: 9px;
// }

// .cayep-community-reply-row + .cayep-community-reply-row {
//     margin-top: 8px;
// }

// .cayep-community-reply-row.management-reply {
//     border-left: 3px solid #C0750D;
// }

// .cayep-community-reply-row.student-reply {
//     border-left: 3px solid #0F6DAB;
// }

// .cayep-community-reply-avatar {
//     width: 34px;
//     height: 34px;
//     min-width: 34px;
// }

// .cayep-community-reply-area {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-community-reply-meta {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 7px;
//     margin-bottom: 6px;
// }

// .cayep-community-reply-meta > strong {
//     color: #062A4B;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-community-role-badge {
//     display: inline-flex;
//     align-items: center;
//     gap: 4px;
//     padding: 3px 6px;
//     border-radius: 5px;
//     font-size: 8px;
//     font-weight: 800;
// }

// .cayep-community-role-badge.facilitator {
//     background: #FFF1D8;
//     color: #A45F00;
// }

// .cayep-community-role-badge.learner {
//     background: #EEF5FA;
//     color: #0F6DAB;
// }

// .cayep-community-reply-date {
//     color: #8A96A0;
//     font-size: 9px;
// }

// .cayep-community-reply-bubble {
//     padding: 9px 11px;
//     background: #FFFFFF;
//     border: 1px solid #E5EAEF;
//     border-radius: 8px;
// }

// .cayep-community-reply-bubble p {
//     margin: 0;
//     color: #4D5D6B;
//     font-size: 11px;
//     line-height: 1.6;
//     white-space: pre-wrap;
//     overflow-wrap: anywhere;
//     word-break: break-word;
// }

// .cayep-community-show-more {
//     display: flex;
//     justify-content: center;
//     margin-top: 9px;
// }

// .cayep-community-show-more button {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     padding: 6px 10px;
//     border: 1px solid #D8E2E9;
//     border-radius: 7px;
//     background: #FFFFFF;
//     color: #0F6DAB;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 9px;
//     font-weight: 800;
// }

// .cayep-community-show-more button:hover {
//     border-color: #0F6DAB;
//     color: #062A4B;
// }

// .cayep-community-no-replies {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     padding: 10px;
//     color: #7A8793;
//     font-size: 10px;
//     line-height: 1.5;
// }

// .cayep-community-no-replies svg {
//     color: #0F6DAB;
//     flex-shrink: 0;
// }

// .cayep-community-reply-form {
//     display: flex;
//     align-items: flex-start;
//     gap: 9px;
//     margin-top: 10px;
//     padding: 10px;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 9px;
// }

// .cayep-community-reply-form-avatar {
//     width: 32px;
//     height: 32px;
//     min-width: 32px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 50%;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 12px;
// }

// .cayep-community-reply-form-main {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-community-reply-form textarea {
//     display: block;
//     width: 100%;
//     min-height: 75px;
//     box-sizing: border-box;
//     padding: 9px 10px;
//     border: 1px solid #D5DEE5;
//     border-radius: 7px;
//     outline: none;
//     resize: vertical;
//     background: #FFFFFF;
//     color: #142536;
//     font-family: inherit;
//     font-size: 11px;
//     line-height: 1.5;
// }

// .cayep-community-reply-form textarea:focus {
//     border-color: #0F6DAB;
//     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.07);
// }

// .cayep-community-reply-form textarea:disabled {
//     background: #F7F9FB;
//     cursor: not-allowed;
// }

// .cayep-community-reply-form-footer {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 10px;
//     margin-top: 7px;
// }

// .cayep-community-reply-form-footer > span {
//     color: #7A8793;
//     font-size: 9px;
// }

// .cayep-community-reply-form-footer button {
//     padding: 7px 10px;
//     background: #063A6B;
//     font-size: 10px;
// }

// .cayep-community-empty {
//     display: flex;
//     align-items: center;
//     flex-direction: column;
//     justify-content: center;
//     min-height: 180px;
//     padding: 25px;
//     text-align: center;
//     background: #FFFFFF;
//     border: 1px dashed #D8E2E9;
//     border-radius: 12px;
// }

// .cayep-community-empty > svg {
//     margin-bottom: 9px;
//     color: #0F6DAB;
//     font-size: 25px;
// }

// .cayep-community-empty h4 {
//     margin: 0 0 5px;
//     color: #062A4B;
//     font-size: 13px;
//     font-weight: 800;
// }

// .cayep-community-empty p {
//     max-width: 480px;
//     margin: 0;
//     color: #7A8793;
//     font-size: 10px;
//     line-height: 1.6;
// }

// .cayep-community-loading-more {
//     display: flex;
//     justify-content: center;
//     margin-top: 10px;
//     color: #7A8793;
//     font-size: 9px;
// }

// @media (max-width: 700px) {
//     .cayep-classroom-question-form {
//         padding: 12px;
//     }

//     .cayep-community-conversation {
//         margin-left: 15px;
//     }

//     .cayep-community-summary {
//         align-items: flex-start;
//     }
// }

// @media (max-width: 480px) {
//     .cayep-classroom-community-banner {
//         padding: 12px;
//     }

//     .cayep-classroom-question-form {
//         flex-direction: column;
//     }

//     .cayep-classroom-form-avatar {
//         width: 34px;
//         height: 34px;
//         min-width: 34px;
//     }

//     .cayep-classroom-form-footer {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-classroom-form-footer button {
//         width: 100%;
//     }

//     .cayep-community-question-row {
//         padding: 12px;
//     }

//     .cayep-community-avatar {
//         width: 34px;
//         height: 34px;
//         min-width: 34px;
//     }

//     .cayep-community-conversation {
//         margin: 0 12px 12px;
//     }

//     .cayep-community-reply-form {
//         flex-direction: column;
//     }

//     .cayep-community-reply-form-footer {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-community-reply-form-footer button {
//         width: 100%;
//     }
// }
// `;

// const QuestionAndAnswer = ({
//     course,
//     classroom,
//     setClassroom,
//     isStudent,
//     isManagement,
//     videoType = "introduction",
//     videoSection = null,
//     videoContextLabel = "Introduction Video",
//     loadClassroom,
// }) => {
//     const [questionText, setQuestionText] = useState("");
//     const [replyTexts, setReplyTexts] = useState({});
//     const [expandedQuestions, setExpandedQuestions] = useState({});
//     const [visibleReplyCounts, setVisibleReplyCounts] = useState({});
//     const [savingQuestion, setSavingQuestion] = useState(false);
//     const [savingReplyId, setSavingReplyId] = useState(null);
//     const [communityDenied, setCommunityDenied] = useState(false);

//     const questionInputRef = useRef(null);
//     const mountedRef = useRef(true);

//     const courseId = course?.id;

//     const isSectionVideo =
//         videoType === "section" &&
//         Boolean(
//             videoSection?.id ||
//                 videoSection?.section_id ||
//                 videoSection?.sectionId
//         );

//     const sectionId = isSectionVideo
//         ? videoSection?.id ||
//           videoSection?.section_id ||
//           videoSection?.sectionId ||
//           null
//         : null;

//     useEffect(() => {
//         mountedRef.current = true;

//         return () => {
//             mountedRef.current = false;
//         };
//     }, []);

//     useEffect(() => {
//         setCommunityDenied(false);
//         setExpandedQuestions({});
//         setVisibleReplyCounts({});
//         setReplyTexts({});
//         setQuestionText("");
//     }, [
//         courseId,
//         sectionId,
//         videoType,
//     ]);

//     const questions = useMemo(() => {
//         if (!Array.isArray(classroom?.questions)) {
//             return [];
//         }

//         return classroom.questions.filter(
//             (question) =>
//                 question &&
//                 typeof question === "object"
//         );
//     }, [classroom?.questions]);

//     const questionsPath = useCallback(() => {
//         if (!courseId) {
//             return "";
//         }

//         if (isSectionVideo && sectionId) {
//             return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/`;
//         }

//         return `api/users/subjects/${courseId}/questions/`;
//     }, [
//         courseId,
//         isSectionVideo,
//         sectionId,
//     ]);

//     const replyPath = useCallback(
//         (questionId) => {
//             if (!courseId || !questionId) {
//                 return "";
//             }

//             if (isSectionVideo && sectionId) {
//                 return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/${questionId}/reply/`;
//             }

//             return `api/users/subjects/${courseId}/questions/${questionId}/reply/`;
//         },
//         [
//             courseId,
//             isSectionVideo,
//             sectionId,
//         ]
//     );

//     const getErrorMessage = useCallback(
//         (error, fallback) => {
//             const data = error?.response?.data;

//             if (typeof data === "string") {
//                 return data;
//             }

//             if (data?.detail) {
//                 return String(data.detail);
//             }

//             if (data?.message) {
//                 return String(data.message);
//             }

//             if (data?.error) {
//                 return String(data.error);
//             }

//             const fields = [
//                 "question",
//                 "reply",
//                 "content",
//             ];

//             for (const field of fields) {
//                 if (data?.[field]) {
//                     return Array.isArray(data[field])
//                         ? data[field].join(" ")
//                         : String(data[field]);
//                 }
//             }

//             if (
//                 error?.response?.status === 403
//             ) {
//                 return "You no longer have access to this course community.";
//             }

//             if (
//                 error?.response?.status === 401
//             ) {
//                 return "Your session has expired. Please sign in again.";
//             }

//             return fallback;
//         },
//         []
//     );

//     const isAccessDeniedError = useCallback(
//         (error) => {
//             const responseStatus =
//                 error?.response?.status;

//             return (
//                 responseStatus === 401 ||
//                 responseStatus === 403
//             );
//         },
//         []
//     );

//     const formatDate = useCallback(
//         (value) => {
//             if (!value) {
//                 return "";
//             }

//             const date = new Date(value);

//             if (Number.isNaN(date.getTime())) {
//                 return "";
//             }

//             try {
//                 return new Intl.DateTimeFormat(
//                     "en-US",
//                     {
//                         dateStyle: "medium",
//                         timeStyle: "short",
//                     }
//                 ).format(date);
//             } catch {
//                 return "";
//             }
//         },
//         []
//     );

//     const getQuestionReplies = useCallback(
//         (question) => {
//             if (Array.isArray(question?.replies)) {
//                 return question.replies;
//             }

//             if (
//                 Array.isArray(
//                     question?.question_replies
//                 )
//             ) {
//                 return question.question_replies;
//             }

//             if (
//                 Array.isArray(question?.answers)
//             ) {
//                 return question.answers;
//             }

//             return [];
//         },
//         []
//     );

//     const getQuestionAvatar = useCallback(
//         (question) =>
//             question?.student_avatar ||
//             question?.student?.avatar ||
//             question?.student_image ||
//             question?.student?.image ||
//             defaultAvatar,
//         []
//     );

//     const getQuestionAuthor = useCallback(
//         (question) =>
//             question?.student_name ||
//             question?.student?.name ||
//             question?.student?.username ||
//             question?.student_username ||
//             "Learner",
//         []
//     );

//     const getReplyAuthor = useCallback(
//         (reply) =>
//             reply?.author_name ||
//             reply?.author?.name ||
//             reply?.author?.username ||
//             reply?.student_name ||
//             reply?.student?.name ||
//             "Learner",
//         []
//     );

//     const getReplyAvatar = useCallback(
//         (reply) =>
//             reply?.author_avatar ||
//             reply?.author?.avatar ||
//             reply?.author?.image ||
//             reply?.student_avatar ||
//             reply?.student?.avatar ||
//             reply?.student?.image ||
//             defaultAvatar,
//         []
//     );

//     const getQuestionRole = useCallback(
//         (question) => {
//             const role = String(
//                 question?.student?.user_type ||
//                     question?.student?.role ||
//                     question?.student?.user_role ||
//                     question?.student_role ||
//                     "student"
//             )
//                 .trim()
//                 .toLowerCase();

//             return MANAGEMENT_ROLES.has(role)
//                 ? "facilitator"
//                 : "learner";
//         },
//         []
//     );

//     const isReplyFromManagement = useCallback(
//         (reply) => {
//             const replyRole = String(
//                 reply?.author_role ||
//                     reply?.author?.user_type ||
//                     reply?.author?.role ||
//                     reply?.author?.user_role ||
//                     ""
//             )
//                 .trim()
//                 .toLowerCase();

//             if (
//                 MANAGEMENT_ROLES.has(replyRole)
//             ) {
//                 return true;
//             }

//             return (
//                 reply?.is_facilitator === true ||
//                 reply?.is_teacher === true ||
//                 reply?.is_management === true
//             );
//         },
//         []
//     );

//     const updateQuestions = useCallback(
//         (updater) => {
//             setClassroom((previous) => {
//                 const previousQuestions =
//                     Array.isArray(
//                         previous?.questions
//                     )
//                         ? previous.questions
//                         : [];

//                 const nextQuestions =
//                     typeof updater === "function"
//                         ? updater(previousQuestions)
//                         : updater;

//                 return {
//                     ...previous,
//                     questions: Array.isArray(
//                         nextQuestions
//                     )
//                         ? nextQuestions
//                         : previousQuestions,
//                 };
//             });
//         },
//         [setClassroom]
//     );

//     const submitQuestion = useCallback(
//         async (event) => {
//             event.preventDefault();

//             const text = questionText
//                 .trim()
//                 .slice(
//                     0,
//                     MAX_QUESTION_LENGTH
//                 );

//             if (
//                 !isStudent ||
//                 communityDenied ||
//                 !text ||
//                 savingQuestion
//             ) {
//                 return;
//             }

//             const endpoint = questionsPath();

//             if (!endpoint) {
//                 return;
//             }

//             setSavingQuestion(true);

//             try {
//                 const response = await api.post(
//                     endpoint,
//                     {
//                         question: text,
//                     }
//                 );

//                 const createdQuestion =
//                     response?.data;

//                 if (
//                     createdQuestion &&
//                     typeof createdQuestion ===
//                         "object"
//                 ) {
//                     updateQuestions(
//                         (previousQuestions) => {
//                             const exists =
//                                 previousQuestions.some(
//                                     (item) =>
//                                         String(
//                                             item?.id
//                                         ) ===
//                                         String(
//                                             createdQuestion?.id
//                                         )
//                                 );

//                             if (exists) {
//                                 return previousQuestions;
//                             }

//                             return [
//                                 createdQuestion,
//                                 ...previousQuestions,
//                             ];
//                         }
//                     );
//                 }

//                 if (mountedRef.current) {
//                     setQuestionText("");
//                     setCommunityDenied(false);
//                 }

//                 Swal.fire({
//                     icon: "success",
//                     title: "Question Posted",
//                     text:
//                         "Your question has been added to the learning community.",
//                     timer: 1400,
//                     showConfirmButton: false,
//                 });

//                 if (
//                     typeof loadClassroom ===
//                     "function"
//                 ) {
//                     try {
//                         await loadClassroom(false);
//                     } catch (refreshError) {
//                         console.warn(
//                             "Community refresh after question failed:",
//                             refreshError
//                         );
//                     }
//                 }
//             } catch (error) {
//                 console.error(
//                     "Unable to submit question:",
//                     error
//                 );

//                 if (
//                     isAccessDeniedError(error) &&
//                     mountedRef.current
//                 ) {
//                     setCommunityDenied(true);
//                 }

//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         error?.response?.status ===
//                         403
//                             ? "Community Access Changed"
//                             : "Question Not Posted",
//                     text: getErrorMessage(
//                         error,
//                         "Your question could not be posted."
//                     ),
//                     confirmButtonColor:
//                         "#062A4B",
//                 });
//             } finally {
//                 if (mountedRef.current) {
//                     setSavingQuestion(false);
//                 }
//             }
//         },
//         [
//             questionText,
//             isStudent,
//             communityDenied,
//             savingQuestion,
//             questionsPath,
//             updateQuestions,
//             loadClassroom,
//             getErrorMessage,
//             isAccessDeniedError,
//         ]
//     );

//     const submitReply = useCallback(
//         async (event, questionId) => {
//             event.preventDefault();

//             const text = String(
//                 replyTexts[questionId] || ""
//             )
//                 .trim()
//                 .slice(
//                     0,
//                     MAX_REPLY_LENGTH
//                 );

//             if (
//                 !questionId ||
//                 !text ||
//                 savingReplyId !== null ||
//                 communityDenied
//             ) {
//                 return;
//             }

//             if (!isStudent && !isManagement) {
//                 return;
//             }

//             const endpoint =
//                 replyPath(questionId);

//             if (!endpoint) {
//                 return;
//             }

//             setSavingReplyId(questionId);

//             try {
//                 const response = await api.post(
//                     endpoint,
//                     {
//                         reply: text,
//                     }
//                 );

//                 const createdReply =
//                     response?.data;

//                 if (
//                     createdReply &&
//                     typeof createdReply ===
//                         "object"
//                 ) {
//                     updateQuestions(
//                         (previousQuestions) =>
//                             previousQuestions.map(
//                                 (question) => {
//                                     if (
//                                         String(
//                                             question?.id
//                                         ) !==
//                                         String(
//                                             questionId
//                                         )
//                                     ) {
//                                         return question;
//                                     }

//                                     const existingReplies =
//                                         getQuestionReplies(
//                                             question
//                                         );

//                                     const alreadyExists =
//                                         existingReplies.some(
//                                             (reply) =>
//                                                 String(
//                                                     reply?.id
//                                                 ) ===
//                                                 String(
//                                                     createdReply?.id
//                                                 )
//                                         );

//                                     if (
//                                         alreadyExists
//                                     ) {
//                                         return question;
//                                     }

//                                     return {
//                                         ...question,
//                                         replies: [
//                                             ...existingReplies,
//                                             createdReply,
//                                         ],
//                                     };
//                                 }
//                             )
//                     );
//                 }

//                 if (mountedRef.current) {
//                     setReplyTexts(
//                         (previous) => ({
//                             ...previous,
//                             [questionId]: "",
//                         })
//                     );

//                     setExpandedQuestions(
//                         (previous) => ({
//                             ...previous,
//                             [questionId]: true,
//                         })
//                     );

//                     setVisibleReplyCounts(
//                         (previous) => ({
//                             ...previous,
//                             [questionId]:
//                                 Math.max(
//                                     previous[
//                                         questionId
//                                     ] ||
//                                         INITIAL_REPLIES_VISIBLE,
//                                     getQuestionReplies(
//                                         questions.find(
//                                             (
//                                                 question
//                                             ) =>
//                                                 String(
//                                                     question?.id
//                                                 ) ===
//                                                 String(
//                                                     questionId
//                                                 )
//                                         )
//                                     ).length + 1
//                                 ),
//                         })
//                     );

//                     setCommunityDenied(false);
//                 }

//                 Swal.fire({
//                     icon: "success",
//                     title: "Reply Posted",
//                     text:
//                         "Your response has been added to the discussion.",
//                     timer: 1100,
//                     showConfirmButton: false,
//                 });

//                 if (
//                     typeof loadClassroom ===
//                     "function"
//                 ) {
//                     try {
//                         await loadClassroom(false);
//                     } catch (refreshError) {
//                         console.warn(
//                             "Community refresh after reply failed:",
//                             refreshError
//                         );
//                     }
//                 }
//             } catch (error) {
//                 console.error(
//                     "Unable to submit reply:",
//                     error
//                 );

//                 if (
//                     isAccessDeniedError(error) &&
//                     mountedRef.current
//                 ) {
//                     setCommunityDenied(true);
//                 }

//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         error?.response?.status ===
//                         403
//                             ? "Community Access Changed"
//                             : "Reply Not Posted",
//                     text: getErrorMessage(
//                         error,
//                         "Your reply could not be posted."
//                     ),
//                     confirmButtonColor:
//                         "#062A4B",
//                 });
//             } finally {
//                 if (mountedRef.current) {
//                     setSavingReplyId(null);
//                 }
//             }
//         },
//         [
//             replyTexts,
//             savingReplyId,
//             communityDenied,
//             isStudent,
//             isManagement,
//             replyPath,
//             updateQuestions,
//             getQuestionReplies,
//             questions,
//             loadClassroom,
//             getErrorMessage,
//             isAccessDeniedError,
//         ]
//     );

//     const toggleQuestion = useCallback(
//         (questionId) => {
//             if (!questionId) {
//                 return;
//             }

//             setExpandedQuestions(
//                 (previous) => ({
//                     ...previous,
//                     [questionId]:
//                         !previous[questionId],
//                 })
//             );
//         },
//         []
//     );

//     const showMoreReplies = useCallback(
//         (questionId, totalReplies) => {
//             setVisibleReplyCounts(
//                 (previous) => ({
//                     ...previous,
//                     [questionId]: Math.min(
//                         totalReplies,
//                         (previous[
//                             questionId
//                         ] ||
//                             INITIAL_REPLIES_VISIBLE) +
//                             REPLIES_LOAD_STEP
//                     ),
//                 })
//             );
//         },
//         []
//     );

//     const collapseReplies = useCallback(
//         (questionId) => {
//             setVisibleReplyCounts(
//                 (previous) => ({
//                     ...previous,
//                     [questionId]:
//                         INITIAL_REPLIES_VISIBLE,
//                 })
//             );
//         },
//         []
//     );

//     const handleAvatarError =
//         useCallback((event) => {
//             if (
//                 event?.currentTarget &&
//                 event.currentTarget.src !==
//                     defaultAvatar
//             ) {
//                 event.currentTarget.src =
//                     defaultAvatar;
//             }
//         }, []);

//     const totalReplies = useMemo(
//         () =>
//             questions.reduce(
//                 (total, question) =>
//                     total +
//                     getQuestionReplies(
//                         question
//                     ).length,
//                 0
//             ),
//         [
//             questions,
//             getQuestionReplies,
//         ]
//     );

//     const communityParticipant =
//         Boolean(isStudent || isManagement);

//     return (
//         <>
//             <style>
//                 {QUESTION_AND_ANSWER_STYLES}
//             </style>

//             <section className="cayep-classroom-content-section cayep-community-root">
//                 <div className="cayep-classroom-section-heading">
//                     <div className="cayep-classroom-heading-icon">
//                         <FaQuestionCircle />
//                     </div>

//                     <div>
//                         <span className="cayep-classroom-section-kicker">
//                             LEARNING COMMUNITY
//                         </span>

//                         <h3>
//                             Questions
//                             &amp;
//                             Answers
//                         </h3>

//                         <p>
//                             Ask questions,
//                             share ideas,
//                             reply to fellow
//                             learners, and
//                             receive guidance
//                             from the
//                             facilitation team.
//                         </p>

//                         <div className="cayep-classroom-context-pill">
//                             <FaBookOpen />

//                             <span>
//                                 {
//                                     videoContextLabel
//                                 }
//                             </span>
//                         </div>
//                     </div>
//                 </div>

//                 {communityDenied ? (
//                     <div className="cayep-community-access-notice">
//                         <div className="cayep-community-access-notice-icon">
//                             <FaUsers />
//                         </div>

//                         <div>
//                             <strong>
//                                 Community Access Unavailable
//                             </strong>

//                             <p>
//                                 Your access to
//                                 this course
//                                 community is no
//                                 longer available.
//                                 The course
//                                 enrollment or
//                                 facilitator
//                                 assignment may
//                                 have changed.
//                             </p>
//                         </div>
//                     </div>
//                 ) : (
//                     <>
//                         <div className="cayep-classroom-community-banner">
//                             <div className="cayep-classroom-community-icon">
//                                 <FaUsers />
//                             </div>

//                             <div>
//                                 <strong>
//                                     Course Learning Community
//                                 </strong>

//                                 <p>
//                                     This discussion
//                                     space is
//                                     available to
//                                     active
//                                     participants
//                                     of this course.
//                                     Learners can
//                                     ask questions
//                                     and respond to
//                                     one another,
//                                     while assigned
//                                     facilitators can
//                                     provide
//                                     guidance.
//                                 </p>
//                             </div>
//                         </div>

//                         {isStudent && (
//                             <form
//                                 className="cayep-classroom-question-form"
//                                 onSubmit={
//                                     submitQuestion
//                                 }
//                             >
//                                 <div className="cayep-classroom-form-avatar">
//                                     <FaComment />
//                                 </div>

//                                 <div className="cayep-classroom-form-main">
//                                     <label>
//                                         Start a
//                                         discussion
//                                     </label>

//                                     <textarea
//                                         ref={
//                                             questionInputRef
//                                         }
//                                         value={
//                                             questionText
//                                         }
//                                         onChange={(
//                                             event
//                                         ) =>
//                                             setQuestionText(
//                                                 event
//                                                     .target
//                                                     .value
//                                             )
//                                         }
//                                         placeholder="Ask a question or share an idea about this lesson..."
//                                         rows={4}
//                                         maxLength={
//                                             MAX_QUESTION_LENGTH
//                                         }
//                                         disabled={
//                                             savingQuestion ||
//                                             communityDenied
//                                         }
//                                     />

//                                     <div className="cayep-classroom-form-footer">
//                                         <span>
//                                             {
//                                                 questionText.length
//                                             }
//                                             /
//                                             {
//                                                 MAX_QUESTION_LENGTH
//                                             }
//                                         </span>

//                                         <button
//                                             type="submit"
//                                             disabled={
//                                                 savingQuestion ||
//                                                 communityDenied ||
//                                                 !questionText.trim()
//                                             }
//                                         >
//                                             <FaPaperPlane />

//                                             {savingQuestion
//                                                 ? "Posting..."
//                                                 : "Post Question"}
//                                         </button>
//                                     </div>
//                                 </div>
//                             </form>
//                         )}

//                         {isManagement && (
//                             <div className="cayep-classroom-teacher-notice">
//                                 <div className="cayep-classroom-teacher-notice-icon">
//                                     <FaChalkboardTeacher />
//                                 </div>

//                                 <div>
//                                     <strong>
//                                         Facilitation
//                                         Discussion
//                                     </strong>

//                                     <p>
//                                         Respond to
//                                         learner
//                                         questions,
//                                         clarify
//                                         difficult
//                                         concepts, and
//                                         contribute
//                                         another
//                                         professional
//                                         perspective
//                                         where it adds
//                                         value.
//                                     </p>
//                                 </div>
//                             </div>
//                         )}

//                         <div className="cayep-community-summary">
//                             <div className="cayep-community-summary-left">
//                                 <FaComment className="cayep-community-summary-icon" />

//                                 <span className="cayep-community-summary-text">
//                                     Community
//                                     discussions
//                                 </span>

//                                 <span className="cayep-community-summary-count">
//                                     {
//                                         questions.length
//                                     }
//                                 </span>
//                             </div>

//                             <span className="cayep-community-summary-text">
//                                 {totalReplies}{" "}
//                                 {totalReplies ===
//                                 1
//                                     ? "reply"
//                                     : "replies"}
//                             </span>
//                         </div>

//                         {questions.length ===
//                         0 ? (
//                             <div className="cayep-community-empty">
//                                 <FaUsers />

//                                 <h4>
//                                     {isManagement
//                                         ? "No Learner Discussions Yet"
//                                         : communityParticipant
//                                         ? "Start the Conversation"
//                                         : "No Discussions Available"}
//                                 </h4>

//                                 <p>
//                                     {isManagement
//                                         ? "Learner questions and ideas about this course content will appear here."
//                                         : communityParticipant
//                                         ? "Be the first learner to ask a question or share an idea with the course community."
//                                         : "There are currently no community discussions available."}
//                                 </p>
//                             </div>
//                         ) : (
//                             <div className="cayep-classroom-question-list">
//                                 {questions.map(
//                                     (
//                                         question,
//                                         questionIndex
//                                     ) => {
//                                         const replies =
//                                             getQuestionReplies(
//                                                 question
//                                             );

//                                         const questionKey =
//                                             question?.id ||
//                                             `question-${questionIndex}`;

//                                         const expanded =
//                                             Boolean(
//                                                 expandedQuestions[
//                                                     questionKey
//                                                 ]
//                                             );

//                                         const replySaving =
//                                             String(
//                                                 savingReplyId
//                                             ) ===
//                                             String(
//                                                 question?.id
//                                             );

//                                         const visibleCount =
//                                             Math.min(
//                                                 replies.length,
//                                                 visibleReplyCounts[
//                                                     questionKey
//                                                 ] ||
//                                                     INITIAL_REPLIES_VISIBLE
//                                             );

//                                         const visibleReplies =
//                                             replies.slice(
//                                                 0,
//                                                 visibleCount
//                                             );

//                                         const hasMoreReplies =
//                                             replies.length >
//                                             visibleCount;

//                                         const questionRole =
//                                             getQuestionRole(
//                                                 question
//                                             );

//                                         return (
//                                             <article
//                                                 className="cayep-community-thread"
//                                                 key={
//                                                     questionKey
//                                                 }
//                                             >
//                                                 <div className="cayep-community-question-row">
//                                                     <div className="cayep-community-avatar">
//                                                         <img
//                                                             src={getQuestionAvatar(
//                                                                 question
//                                                             )}
//                                                             alt=""
//                                                             loading="lazy"
//                                                             decoding="async"
//                                                             onError={
//                                                                 handleAvatarError
//                                                             }
//                                                         />
//                                                     </div>

//                                                     <div className="cayep-community-message-area">
//                                                         <div className="cayep-community-author-line">
//                                                             <strong>
//                                                                 {getQuestionAuthor(
//                                                                     question
//                                                                 )}
//                                                             </strong>

//                                                             <span className="cayep-community-author-role">
//                                                                 {questionRole ===
//                                                                 "facilitator" ? (
//                                                                     <>
//                                                                         <FaChalkboardTeacher />
//                                                                         Facilitator
//                                                                     </>
//                                                                 ) : (
//                                                                     <>
//                                                                         <FaUser />
//                                                                         Learner
//                                                                     </>
//                                                                 )}
//                                                             </span>

//                                                             <span className="date">
//                                                                 {formatDate(
//                                                                     question?.created_at ||
//                                                                         question?.createdAt
//                                                                 )}
//                                                             </span>
//                                                         </div>

//                                                         <div className="cayep-community-question-bubble">
//                                                             <span className="cayep-community-quote-mark">
//                                                                 “
//                                                             </span>

//                                                             <p>
//                                                                 {
//                                                                     question?.question
//                                                                 }
//                                                             </p>
//                                                         </div>

//                                                         <div className="cayep-community-message-actions">
//                                                             <button
//                                                                 type="button"
//                                                                 onClick={() =>
//                                                                     toggleQuestion(
//                                                                         questionKey
//                                                                     )
//                                                                 }
//                                                                 aria-expanded={
//                                                                     expanded
//                                                                 }
//                                                             >
//                                                                 {expanded ? (
//                                                                     <FaChevronUp />
//                                                                 ) : (
//                                                                     <FaReply />
//                                                                 )}

//                                                                 {expanded
//                                                                     ? "Hide Discussion"
//                                                                     : `Join Discussion${
//                                                                           replies.length
//                                                                               ? ` (${replies.length})`
//                                                                               : ""
//                                                                       }`}
//                                                             </button>
//                                                         </div>
//                                                     </div>
//                                                 </div>

//                                                 {expanded && (
//                                                     <div className="cayep-community-conversation">
//                                                         <div className="cayep-community-replies-header">
//                                                             <strong>
//                                                                 Discussion
//                                                             </strong>

//                                                             <span>
//                                                                 {
//                                                                     replies.length
//                                                                 }{" "}
//                                                                 {replies.length ===
//                                                                 1
//                                                                     ? "reply"
//                                                                     : "replies"}
//                                                             </span>
//                                                         </div>

//                                                         {replies.length >
//                                                         0 ? (
//                                                             <>
//                                                                 <div className="cayep-community-reply-list">
//                                                                     {visibleReplies.map(
//                                                                         (
//                                                                             reply,
//                                                                             replyIndex
//                                                                         ) => {
//                                                                             const managementReply =
//                                                                                 isReplyFromManagement(
//                                                                                     reply
//                                                                                 );

//                                                                             const replyKey =
//                                                                                 reply?.id ||
//                                                                                 `${questionKey}-reply-${replyIndex}`;

//                                                                             return (
//                                                                                 <div
//                                                                                     className={`cayep-community-reply-row ${
//                                                                                         managementReply
//                                                                                             ? "management-reply"
//                                                                                             : "student-reply"
//                                                                                     }`}
//                                                                                     key={
//                                                                                         replyKey
//                                                                                     }
//                                                                                 >
//                                                                                     <div className="cayep-community-reply-avatar">
//                                                                                         <img
//                                                                                             src={getReplyAvatar(
//                                                                                                 reply
//                                                                                             )}
//                                                                                             alt=""
//                                                                                             loading="lazy"
//                                                                                             decoding="async"
//                                                                                             onError={
//                                                                                                 handleAvatarError
//                                                                                             }
//                                                                                         />
//                                                                                     </div>

//                                                                                     <div className="cayep-community-reply-area">
//                                                                                         <div className="cayep-community-reply-meta">
//                                                                                             <strong>
//                                                                                                 {getReplyAuthor(
//                                                                                                     reply
//                                                                                                 )}
//                                                                                             </strong>

//                                                                                             <span
//                                                                                                 className={`cayep-community-role-badge ${
//                                                                                                     managementReply
//                                                                                                         ? "facilitator"
//                                                                                                         : "learner"
//                                                                                                 }`}
//                                                                                             >
//                                                                                                 {managementReply ? (
//                                                                                                     <>
//                                                                                                         <FaChalkboardTeacher />
//                                                                                                         Facilitator
//                                                                                                     </>
//                                                                                                 ) : (
//                                                                                                     <>
//                                                                                                         <FaUser />
//                                                                                                         Learner
//                                                                                                     </>
//                                                                                                 )}
//                                                                                             </span>

//                                                                                             <span className="cayep-community-reply-date">
//                                                                                                 {formatDate(
//                                                                                                     reply?.created_at ||
//                                                                                                         reply?.createdAt
//                                                                                                 )}
//                                                                                             </span>
//                                                                                         </div>

//                                                                                         <div className="cayep-community-reply-bubble">
//                                                                                             <p>
//                                                                                                 {
//                                                                                                     reply?.reply
//                                                                                                 }
//                                                                                             </p>
//                                                                                         </div>
//                                                                                     </div>
//                                                                                 </div>
//                                                                             );
//                                                                         }
//                                                                     )}
//                                                                 </div>

//                                                                 {hasMoreReplies && (
//                                                                     <div className="cayep-community-show-more">
//                                                                         <button
//                                                                             type="button"
//                                                                             onClick={() =>
//                                                                                 showMoreReplies(
//                                                                                     questionKey,
//                                                                                     replies.length
//                                                                                 )
//                                                                             }
//                                                                         >
//                                                                             <FaChevronDown />

//                                                                             Show{" "}
//                                                                             {Math.min(
//                                                                                 REPLIES_LOAD_STEP,
//                                                                                 replies.length -
//                                                                                     visibleCount
//                                                                             )}{" "}
//                                                                             more
//                                                                             replies
//                                                                         </button>
//                                                                     </div>
//                                                                 )}

//                                                                 {!hasMoreReplies &&
//                                                                     replies.length >
//                                                                         INITIAL_REPLIES_VISIBLE && (
//                                                                         <div className="cayep-community-show-more">
//                                                                             <button
//                                                                                 type="button"
//                                                                                 onClick={() =>
//                                                                                     collapseReplies(
//                                                                                         questionKey
//                                                                                     )
//                                                                                 }
//                                                                             >
//                                                                                 <FaChevronUp />

//                                                                                 Show
//                                                                                 fewer
//                                                                             </button>
//                                                                         </div>
//                                                                     )}
//                                                             </>
//                                                         ) : (
//                                                             <div className="cayep-community-no-replies">
//                                                                 <FaComment />

//                                                                 <span>
//                                                                     No
//                                                                     replies
//                                                                     yet.
//                                                                     Start
//                                                                     the
//                                                                     conversation
//                                                                     and
//                                                                     share
//                                                                     your
//                                                                     perspective.
//                                                                 </span>
//                                                             </div>
//                                                         )}

//                                                         {(isStudent ||
//                                                             isManagement) &&
//                                                             !communityDenied && (
//                                                                 <form
//                                                                     className="cayep-community-reply-form"
//                                                                     onSubmit={(
//                                                                         event
//                                                                     ) =>
//                                                                         submitReply(
//                                                                             event,
//                                                                             question?.id
//                                                                         )
//                                                                     }
//                                                                 >
//                                                                     <div className="cayep-community-reply-form-avatar">
//                                                                         {isManagement ? (
//                                                                             <FaChalkboardTeacher />
//                                                                         ) : (
//                                                                             <FaUser />
//                                                                         )}
//                                                                     </div>

//                                                                     <div className="cayep-community-reply-form-main">
//                                                                         <textarea
//                                                                             value={
//                                                                                 replyTexts[
//                                                                                     question?.id
//                                                                                 ] ||
//                                                                                 ""
//                                                                             }
//                                                                             onChange={(
//                                                                                 event
//                                                                             ) =>
//                                                                                 setReplyTexts(
//                                                                                     (
//                                                                                         previous
//                                                                                     ) => ({
//                                                                                         ...previous,
//                                                                                         [question?.id]:
//                                                                                             event
//                                                                                                 .target
//                                                                                                 .value
//                                                                                                 .slice(
//                                                                                                     0,
//                                                                                                     MAX_REPLY_LENGTH
//                                                                                                 ),
//                                                                                     })
//                                                                                 )
//                                                                             }
//                                                                             placeholder={
//                                                                                 isManagement
//                                                                                     ? "Share guidance, an explanation, or another professional perspective..."
//                                                                                     : "Reply to this learner..."
//                                                                             }
//                                                                             rows={
//                                                                                 3
//                                                                             }
//                                                                             maxLength={
//                                                                                 MAX_REPLY_LENGTH
//                                                                             }
//                                                                             disabled={
//                                                                                 replySaving ||
//                                                                                 communityDenied
//                                                                             }
//                                                                         />

//                                                                         <div className="cayep-community-reply-form-footer">
//                                                                             <span>
//                                                                                 {
//                                                                                     String(
//                                                                                         replyTexts[
//                                                                                             question?.id
//                                                                                         ] ||
//                                                                                             ""
//                                                                                     ).length
//                                                                                 }
//                                                                                 /
//                                                                                 {
//                                                                                     MAX_REPLY_LENGTH
//                                                                                 }
//                                                                             </span>

//                                                                             <button
//                                                                                 type="submit"
//                                                                                 disabled={
//                                                                                     replySaving ||
//                                                                                     communityDenied ||
//                                                                                     !String(
//                                                                                         replyTexts[
//                                                                                             question?.id
//                                                                                         ] ||
//                                                                                             ""
//                                                                                     ).trim()
//                                                                                 }
//                                                                             >
//                                                                                 <FaPaperPlane />

//                                                                                 {replySaving
//                                                                                     ? "Sending..."
//                                                                                     : "Reply"}
//                                                                             </button>
//                                                                         </div>
//                                                                     </div>
//                                                                 </form>
//                                                             )}
//                                                     </div>
//                                                 )}
//                                             </article>
//                                         );
//                                     }
//                                 )}
//                             </div>
//                         )}
//                     </>
//                 )}
//             </section>
//         </>
//     );
// };

// export default QuestionAndAnswer;







// import React, {
//     useCallback,
//     useState,
// } from "react";

// import {
//     FaBookOpen,
//     FaChalkboardTeacher,
//     FaComment,
//     FaPaperPlane,
//     FaQuestionCircle,
//     FaReply,
//     FaUser,
//     FaUsers,
// } from "react-icons/fa";

// import Swal from "sweetalert2";

// import api from "../api/api";
// import defaultAvatar from "./learningenvironment.jpg";

// const MANAGEMENT_ROLES = new Set([
//     "teacher",
//     "facilitator",
//     "instructor",
//     "admin",
//     "administrator",
//     "principal",
//     "proprietor",
// ]);

// const QUESTION_AND_ANSWER_STYLES = `
// .cayep-classroom-community-banner {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     margin-bottom: 18px;
//     padding: 15px;
//     background: #F2F8FC;
//     border: 1px solid #D8E8F2;
//     border-radius: 11px;
// }

// .cayep-classroom-community-icon {
//     width: 38px;
//     height: 38px;
//     min-width: 38px;
//     border-radius: 10px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #FFFFFF;
//     color: #0F6DAB;
//     border: 1px solid #D8E8F2;
//     font-size: 16px;
// }

// .cayep-classroom-community-banner strong {
//     display: block;
//     margin-bottom: 4px;
//     color: #062A4B;
//     font-size: 13px;
//     font-weight: 800;
// }

// .cayep-classroom-community-banner p {
//     margin: 0;
//     color: #596978;
//     font-size: 11px;
//     line-height: 1.55;
// }

// .cayep-classroom-question-form {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     margin-bottom: 20px;
//     padding: 15px;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 11px;
// }

// .cayep-classroom-form-avatar {
//     width: 38px;
//     height: 38px;
//     min-width: 38px;
//     border-radius: 50%;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 14px;
// }

// .cayep-classroom-form-main {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-classroom-form-main label {
//     display: block;
//     margin-bottom: 7px;
//     color: #062A4B;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-classroom-form-main textarea {
//     display: block;
//     width: 100%;
//     min-height: 100px;
//     box-sizing: border-box;
//     padding: 11px 12px;
//     border: 1px solid #D5DEE5;
//     border-radius: 8px;
//     outline: none;
//     resize: vertical;
//     background: #FFFFFF;
//     color: #142536;
//     font-family: inherit;
//     font-size: 12px;
//     line-height: 1.55;
// }

// .cayep-classroom-form-main textarea:focus {
//     border-color: #0F6DAB;
//     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
// }

// .cayep-classroom-form-main textarea:disabled {
//     background: #F7F9FB;
//     cursor: not-allowed;
// }

// .cayep-classroom-form-footer {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 10px;
//     margin-top: 8px;
// }

// .cayep-classroom-form-footer > span {
//     color: #7A8793;
//     font-size: 10px;
// }

// .cayep-classroom-form-footer button {
//     display: inline-flex;
//     align-items: center;
//     gap: 7px;
//     border: 0;
//     border-radius: 8px;
//     padding: 9px 13px;
//     background: #062A4B;
//     color: #FFFFFF;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-classroom-form-footer button:hover {
//     background: #063A6B;
// }

// .cayep-classroom-form-footer button:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
// }

// .cayep-classroom-teacher-notice {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     margin-bottom: 18px;
//     padding: 13px 15px;
//     background: #FFF9E8;
//     border: 1px solid #F0DE9E;
//     border-radius: 10px;
// }

// .cayep-classroom-teacher-notice-icon {
//     width: 36px;
//     height: 36px;
//     min-width: 36px;
//     border-radius: 9px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #FFF1C2;
//     color: #C0750D;
// }

// .cayep-classroom-teacher-notice strong {
//     display: block;
//     margin-bottom: 3px;
//     color: #6B4B00;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-classroom-teacher-notice p {
//     margin: 0;
//     color: #806A32;
//     font-size: 11px;
//     line-height: 1.55;
// }

// .cayep-classroom-question-list {
//     display: flex;
//     flex-direction: column;
//     gap: 12px;
// }

// .cayep-community-thread {
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 12px;
//     overflow: hidden;
// }

// .cayep-community-question-row {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     padding: 15px;
// }

// .cayep-community-avatar,
// .cayep-community-reply-avatar {
//     width: 40px;
//     height: 40px;
//     min-width: 40px;
//     border-radius: 50%;
//     overflow: hidden;
//     background: #EEF5FA;
//     border: 1px solid #DCE7EE;
// }

// .cayep-community-avatar img,
// .cayep-community-reply-avatar img {
//     width: 100%;
//     height: 100%;
//     display: block;
//     object-fit: cover;
// }

// .cayep-community-message-area {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-community-author-line {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 8px;
//     margin-bottom: 7px;
// }

// .cayep-community-author-line strong {
//     color: #062A4B;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-community-author-line span {
//     color: #8A96A0;
//     font-size: 9px;
// }

// .cayep-community-question-bubble {
//     position: relative;
//     padding: 12px 14px 12px 26px;
//     background: #F7F9FB;
//     border: 1px solid #E4EAEF;
//     border-radius: 10px;
// }

// .cayep-community-question-bubble p {
//     margin: 0;
//     color: #394B5A;
//     font-size: 12px;
//     line-height: 1.65;
//     white-space: pre-wrap;
//     overflow-wrap: anywhere;
// }

// .cayep-community-quote-mark {
//     position: absolute;
//     top: 5px;
//     left: 9px;
//     color: #C0750D;
//     font-size: 20px;
//     font-weight: 800;
//     line-height: 1;
// }

// .cayep-community-message-actions {
//     display: flex;
//     align-items: center;
//     margin-top: 8px;
// }

// .cayep-community-message-actions button {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     padding: 5px 0;
//     border: 0;
//     background: transparent;
//     color: #0F6DAB;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 10px;
//     font-weight: 800;
// }

// .cayep-community-message-actions button:hover {
//     color: #062A4B;
// }

// .cayep-community-conversation {
//     margin: 0 15px 15px 67px;
//     padding: 12px;
//     background: #F7F9FB;
//     border: 1px solid #E0E7EC;
//     border-radius: 10px;
// }

// .cayep-community-reply-row {
//     display: flex;
//     align-items: flex-start;
//     gap: 10px;
//     padding: 10px;
//     background: #FFFFFF;
//     border: 1px solid #E4EAEF;
//     border-radius: 9px;
// }

// .cayep-community-reply-row + .cayep-community-reply-row {
//     margin-top: 8px;
// }

// .cayep-community-reply-row.management-reply {
//     border-left: 3px solid #C0750D;
// }

// .cayep-community-reply-row.student-reply {
//     border-left: 3px solid #0F6DAB;
// }

// .cayep-community-reply-avatar {
//     width: 34px;
//     height: 34px;
//     min-width: 34px;
// }

// .cayep-community-reply-area {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-community-reply-meta {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 7px;
//     margin-bottom: 6px;
// }

// .cayep-community-reply-meta > strong {
//     color: #062A4B;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-community-role-badge {
//     display: inline-flex;
//     align-items: center;
//     gap: 4px;
//     padding: 3px 6px;
//     border-radius: 5px;
//     font-size: 8px;
//     font-weight: 800;
// }

// .cayep-community-role-badge.facilitator {
//     background: #FFF1D8;
//     color: #A45F00;
// }

// .cayep-community-role-badge.learner {
//     background: #EEF5FA;
//     color: #0F6DAB;
// }

// .cayep-community-reply-date {
//     color: #8A96A0;
//     font-size: 9px;
// }

// .cayep-community-reply-bubble {
//     padding: 9px 11px;
//     background: #FFFFFF;
//     border: 1px solid #E5EAEF;
//     border-radius: 8px;
// }

// .cayep-community-reply-bubble p {
//     margin: 0;
//     color: #4D5D6B;
//     font-size: 11px;
//     line-height: 1.6;
//     white-space: pre-wrap;
//     overflow-wrap: anywhere;
// }

// .cayep-community-no-replies {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     padding: 10px;
//     color: #7A8793;
//     font-size: 10px;
//     line-height: 1.5;
// }

// .cayep-community-no-replies svg {
//     color: #0F6DAB;
//     flex-shrink: 0;
// }

// .cayep-community-reply-form {
//     display: flex;
//     align-items: flex-start;
//     gap: 9px;
//     margin-top: 10px;
//     padding: 10px;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 9px;
// }

// .cayep-community-reply-form-avatar {
//     width: 32px;
//     height: 32px;
//     min-width: 32px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 50%;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 12px;
// }

// .cayep-community-reply-form-main {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-community-reply-form textarea {
//     display: block;
//     width: 100%;
//     min-height: 75px;
//     box-sizing: border-box;
//     padding: 9px 10px;
//     border: 1px solid #D5DEE5;
//     border-radius: 7px;
//     outline: none;
//     resize: vertical;
//     background: #FFFFFF;
//     color: #142536;
//     font-family: inherit;
//     font-size: 11px;
//     line-height: 1.5;
// }

// .cayep-community-reply-form textarea:focus {
//     border-color: #0F6DAB;
//     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.07);
// }

// .cayep-community-reply-form textarea:disabled {
//     background: #F7F9FB;
//     cursor: not-allowed;
// }

// .cayep-community-reply-form-footer {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 10px;
//     margin-top: 7px;
// }

// .cayep-community-reply-form-footer > span {
//     color: #7A8793;
//     font-size: 9px;
// }

// .cayep-community-reply-form-footer button {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     border: 0;
//     border-radius: 7px;
//     padding: 7px 10px;
//     background: #063A6B;
//     color: #FFFFFF;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 10px;
//     font-weight: 800;
// }

// .cayep-community-reply-form-footer button:hover {
//     background: #062A4B;
// }

// .cayep-community-reply-form-footer button:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
// }

// @media (max-width: 700px) {
//     .cayep-classroom-question-form {
//         padding: 12px;
//     }

//     .cayep-community-conversation {
//         margin-left: 15px;
//     }
// }

// @media (max-width: 480px) {
//     .cayep-classroom-community-banner {
//         padding: 12px;
//     }

//     .cayep-classroom-question-form {
//         flex-direction: column;
//     }

//     .cayep-classroom-form-avatar {
//         width: 34px;
//         height: 34px;
//         min-width: 34px;
//     }

//     .cayep-classroom-form-footer {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-classroom-form-footer button {
//         width: 100%;
//         justify-content: center;
//     }

//     .cayep-community-question-row {
//         padding: 12px;
//     }

//     .cayep-community-avatar {
//         width: 34px;
//         height: 34px;
//         min-width: 34px;
//     }

//     .cayep-community-conversation {
//         margin: 0 12px 12px;
//     }

//     .cayep-community-reply-form {
//         flex-direction: column;
//     }

//     .cayep-community-reply-form-footer {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-community-reply-form-footer button {
//         width: 100%;
//         justify-content: center;
//     }
// }
// `;

// const QuestionAndAnswer = ({
//     course,
//     classroom,
//     setClassroom,
//     isStudent,
//     isManagement,
//     videoType = "introduction",
//     videoSection = null,
//     videoContextLabel = "Introduction Video",
//     loadClassroom,
// }) => {
//     const [questionText, setQuestionText] =
//         useState("");

//     const [replyTexts, setReplyTexts] =
//         useState({});

//     const [expandedQuestions, setExpandedQuestions] =
//         useState({});

//     const [savingQuestion, setSavingQuestion] =
//         useState(false);

//     const [savingReplyId, setSavingReplyId] =
//         useState(null);

//     const courseId = course?.id;

//     const isSectionVideo =
//         videoType === "section" &&
//         Boolean(
//             videoSection?.id ||
//                 videoSection?.section_id ||
//                 videoSection?.sectionId
//         );

//     const sectionId = isSectionVideo
//         ? videoSection?.id ||
//           videoSection?.section_id ||
//           videoSection?.sectionId ||
//           null
//         : null;

//     const questionsPath = useCallback(() => {
//         if (!courseId) {
//             return "";
//         }

//         if (
//             isSectionVideo &&
//             sectionId
//         ) {
//             return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/`;
//         }

//         return `api/users/subjects/${courseId}/questions/`;
//     }, [
//         courseId,
//         isSectionVideo,
//         sectionId,
//     ]);

//     const replyPath = useCallback(
//         (questionId) => {
//             if (
//                 !courseId ||
//                 !questionId
//             ) {
//                 return "";
//             }

//             if (
//                 isSectionVideo &&
//                 sectionId
//             ) {
//                 return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/${questionId}/reply/`;
//             }

//             return `api/users/subjects/${courseId}/questions/${questionId}/reply/`;
//         },
//         [
//             courseId,
//             isSectionVideo,
//             sectionId,
//         ]
//     );

//     const getErrorMessage = useCallback(
//         (error, fallback) => {
//             const data =
//                 error?.response?.data;

//             if (
//                 typeof data ===
//                 "string"
//             ) {
//                 return data;
//             }

//             if (data?.detail) {
//                 return data.detail;
//             }

//             if (data?.message) {
//                 return data.message;
//             }

//             if (data?.error) {
//                 return data.error;
//             }

//             if (data?.question) {
//                 return Array.isArray(
//                     data.question
//                 )
//                     ? data.question.join(
//                           " "
//                       )
//                     : data.question;
//             }

//             if (data?.reply) {
//                 return Array.isArray(
//                     data.reply
//                 )
//                     ? data.reply.join(
//                           " "
//                       )
//                     : data.reply;
//             }

//             if (data?.content) {
//                 return Array.isArray(
//                     data.content
//                 )
//                     ? data.content.join(
//                           " "
//                       )
//                     : data.content;
//             }

//             return fallback;
//         },
//         []
//     );

//     const formatDate = useCallback(
//         (value) => {
//             if (!value) {
//                 return "";
//             }

//             const date =
//                 new Date(value);

//             if (
//                 Number.isNaN(
//                     date.getTime()
//                 )
//             ) {
//                 return "";
//             }

//             try {
//                 return new Intl.DateTimeFormat(
//                     "en-US",
//                     {
//                         dateStyle:
//                             "medium",
//                         timeStyle:
//                             "short",
//                     }
//                 ).format(date);
//             } catch {
//                 return "";
//             }
//         },
//         []
//     );

//     const submitQuestion =
//         useCallback(
//             async (event) => {
//                 event.preventDefault();

//                 const text =
//                     questionText.trim();

//                 if (
//                     !isStudent ||
//                     !text ||
//                     savingQuestion
//                 ) {
//                     return;
//                 }

//                 const endpoint =
//                     questionsPath();

//                 if (!endpoint) {
//                     return;
//                 }

//                 setSavingQuestion(true);

//                 try {
//                     const response =
//                         await api.post(
//                             endpoint,
//                             {
//                                 question:
//                                     text,
//                             }
//                         );

//                     const createdQuestion =
//                         response?.data;

//                     if (
//                         createdQuestion &&
//                         typeof createdQuestion ===
//                             "object"
//                     ) {
//                         setClassroom(
//                             (
//                                 previous
//                             ) => ({
//                                 ...previous,
//                                 questions: [
//                                     createdQuestion,
//                                     ...(Array.isArray(
//                                         previous?.questions
//                                     )
//                                         ? previous.questions
//                                         : []),
//                                 ],
//                             })
//                         );
//                     }

//                     setQuestionText("");

//                     await loadClassroom(false);

//                     Swal.fire({
//                         icon: "success",
//                         title: "Question Posted",
//                         text:
//                             "Your question has been added to the learning community.",
//                         timer: 1500,
//                         showConfirmButton: false,
//                     });
//                 } catch (error) {
//                     console.error(
//                         "Unable to submit question:",
//                         error
//                     );

//                     Swal.fire({
//                         icon: "error",
//                         title:
//                             "Question Not Posted",
//                         text: getErrorMessage(
//                             error,
//                             "Your question could not be posted."
//                         ),
//                         confirmButtonColor:
//                             "#062A4B",
//                     });
//                 } finally {
//                     setSavingQuestion(false);
//                 }
//             },
//             [
//                 questionText,
//                 isStudent,
//                 savingQuestion,
//                 questionsPath,
//                 setClassroom,
//                 loadClassroom,
//                 getErrorMessage,
//             ]
//         );

//     const submitReply =
//         useCallback(
//             async (
//                 event,
//                 questionId
//             ) => {
//                 event.preventDefault();

//                 const text =
//                     String(
//                         replyTexts[
//                             questionId
//                         ] || ""
//                     ).trim();

//                 if (
//                     !questionId ||
//                     !text ||
//                     savingReplyId !==
//                         null
//                 ) {
//                     return;
//                 }

//                 if (
//                     !isStudent &&
//                     !isManagement
//                 ) {
//                     return;
//                 }

//                 const endpoint =
//                     replyPath(
//                         questionId
//                     );

//                 if (!endpoint) {
//                     return;
//                 }

//                 setSavingReplyId(
//                     questionId
//                 );

//                 try {
//                     const response =
//                         await api.post(
//                             endpoint,
//                             {
//                                 reply: text,
//                             }
//                         );

//                     const createdReply =
//                         response?.data;

//                     if (
//                         createdReply &&
//                         typeof createdReply ===
//                             "object"
//                     ) {
//                         setClassroom(
//                             (
//                                 previous
//                             ) => ({
//                                 ...previous,
//                                 questions:
//                                     Array.isArray(
//                                         previous?.questions
//                                     )
//                                         ? previous.questions.map(
//                                               (
//                                                   question
//                                               ) => {
//                                                   if (
//                                                       String(
//                                                           question?.id
//                                                       ) !==
//                                                       String(
//                                                           questionId
//                                                       )
//                                                   ) {
//                                                       return question;
//                                                   }

//                                                   return {
//                                                       ...question,
//                                                       replies:
//                                                           [
//                                                               ...(Array.isArray(
//                                                                   question?.replies
//                                                               )
//                                                                   ? question.replies
//                                                                   : []),
//                                                               createdReply,
//                                                           ],
//                                                   };
//                                               }
//                                           )
//                                         : [],
//                             })
//                         );
//                     }

//                     setReplyTexts(
//                         (
//                             previous
//                         ) => ({
//                             ...previous,
//                             [questionId]:
//                                 "",
//                         })
//                     );

//                     setExpandedQuestions(
//                         (
//                             previous
//                         ) => ({
//                             ...previous,
//                             [questionId]:
//                                 true,
//                         })
//                     );

//                     await loadClassroom(false);

//                     Swal.fire({
//                         icon: "success",
//                         title: "Reply Posted",
//                         text:
//                             "Your response has been added to the discussion.",
//                         timer: 1200,
//                         showConfirmButton: false,
//                     });
//                 } catch (error) {
//                     console.error(
//                         "Unable to submit reply:",
//                         error
//                     );

//                     Swal.fire({
//                         icon: "error",
//                         title:
//                             "Reply Not Posted",
//                         text: getErrorMessage(
//                             error,
//                             "Your reply could not be posted."
//                         ),
//                         confirmButtonColor:
//                             "#062A4B",
//                     });
//                 } finally {
//                     setSavingReplyId(null);
//                 }
//             },
//             [
//                 replyTexts,
//                 savingReplyId,
//                 isStudent,
//                 isManagement,
//                 replyPath,
//                 setClassroom,
//                 loadClassroom,
//                 getErrorMessage,
//             ]
//         );

//     const toggleQuestion =
//         useCallback(
//             (questionId) => {
//                 if (!questionId) {
//                     return;
//                 }

//                 setExpandedQuestions(
//                     (previous) => ({
//                         ...previous,
//                         [questionId]:
//                             !previous[
//                                 questionId
//                             ],
//                     })
//                 );
//             },
//             []
//         );

//     const getQuestionAvatar =
//         useCallback(
//             (question) => {
//                 return (
//                     question?.student_avatar ||
//                     question?.student?.avatar ||
//                     question?.student_image ||
//                     question?.student?.image ||
//                     defaultAvatar
//                 );
//             },
//             []
//         );

//     const getQuestionAuthor =
//         useCallback(
//             (question) => {
//                 return (
//                     question?.student_name ||
//                     question?.student?.name ||
//                     question?.student?.username ||
//                     question?.student_username ||
//                     "Learner"
//                 );
//             },
//             []
//         );

//     const getReplyAuthor =
//         useCallback(
//             (reply) => {
//                 return (
//                     reply?.author_name ||
//                     reply?.author?.name ||
//                     reply?.author?.username ||
//                     reply?.student_name ||
//                     reply?.student?.name ||
//                     "Learner"
//                 );
//             },
//             []
//         );

//     const getReplyAvatar =
//         useCallback(
//             (reply) => {
//                 return (
//                     reply?.author_avatar ||
//                     reply?.author?.avatar ||
//                     reply?.author?.image ||
//                     reply?.student_avatar ||
//                     reply?.student?.avatar ||
//                     reply?.student?.image ||
//                     defaultAvatar
//                 );
//             },
//             []
//         );

//     const isReplyFromManagement =
//         useCallback(
//             (reply) => {
//                 const replyRole =
//                     String(
//                         reply?.author_role ||
//                             reply?.author?.user_type ||
//                             reply?.author?.role ||
//                             reply?.author?.user_role ||
//                             ""
//                     )
//                         .trim()
//                         .toLowerCase();

//                 if (
//                     MANAGEMENT_ROLES.has(
//                         replyRole
//                     )
//                 ) {
//                     return true;
//                 }

//                 return (
//                     reply?.is_facilitator === true ||
//                     reply?.is_teacher === true ||
//                     reply?.is_management === true
//                 );
//             },
//             []
//         );

//     const getQuestionReplies =
//         useCallback(
//             (question) => {
//                 if (
//                     Array.isArray(
//                         question?.replies
//                     )
//                 ) {
//                     return question.replies;
//                 }

//                 if (
//                     Array.isArray(
//                         question?.question_replies
//                     )
//                 ) {
//                     return question.question_replies;
//                 }

//                 if (
//                     Array.isArray(
//                         question?.answers
//                     )
//                 ) {
//                     return question.answers;
//                 }

//                 return [];
//             },
//             []
//         );

//     const questions = Array.isArray(
//         classroom?.questions
//     )
//         ? classroom.questions
//         : [];

//     return (
//         <>
//             <style>
//                 {
//                     QUESTION_AND_ANSWER_STYLES
//                 }
//             </style>

//             <section className="cayep-classroom-content-section">
//                 <div className="cayep-classroom-section-heading">
//                     <div className="cayep-classroom-heading-icon">
//                         <FaQuestionCircle />
//                     </div>

//                     <div>
//                         <span className="cayep-classroom-section-kicker">
//                             LEARNING COMMUNITY
//                         </span>

//                         <h3>
//                             Questions
//                             &amp;
//                             Answers
//                         </h3>

//                         <p>
//                             Ask questions,
//                             reply to other
//                             learners, and
//                             receive guidance
//                             from the facilitation
//                             team.
//                         </p>

//                         <div className="cayep-classroom-context-pill">
//                             <FaBookOpen />

//                             <span>
//                                 {
//                                     videoContextLabel
//                                 }
//                             </span>
//                         </div>
//                     </div>
//                 </div>

//                 <div className="cayep-classroom-community-banner">
//                     <div className="cayep-classroom-community-icon">
//                         <FaUsers />
//                     </div>

//                     <div>
//                         <strong>
//                             Learn Together
//                         </strong>

//                         <p>
//                             Ask a question,
//                             share an
//                             explanation, or
//                             build on another
//                             learner&apos;s idea.
//                             Facilitators can
//                             provide guidance
//                             when needed.
//                         </p>
//                     </div>
//                 </div>

//                 {isStudent && (
//                     <form
//                         className="cayep-classroom-question-form"
//                         onSubmit={
//                             submitQuestion
//                         }
//                     >
//                         <div className="cayep-classroom-form-avatar">
//                             <FaComment />
//                         </div>

//                         <div className="cayep-classroom-form-main">
//                             <label>
//                                 Start a
//                                 discussion
//                             </label>

//                             <textarea
//                                 value={
//                                     questionText
//                                 }
//                                 onChange={(
//                                     event
//                                 ) =>
//                                     setQuestionText(
//                                         event
//                                             .target
//                                             .value
//                                     )
//                                 }
//                                 placeholder="Share a question or thought about this video..."
//                                 rows={4}
//                                 maxLength={3000}
//                                 disabled={
//                                     savingQuestion
//                                 }
//                             />

//                             <div className="cayep-classroom-form-footer">
//                                 <span>
//                                     {
//                                         questionText.length
//                                     }
//                                     /3000
//                                 </span>

//                                 <button
//                                     type="submit"
//                                     disabled={
//                                         savingQuestion ||
//                                         !questionText.trim()
//                                     }
//                                 >
//                                     <FaPaperPlane />

//                                     {savingQuestion
//                                         ? "Posting..."
//                                         : "Post Question"}
//                                 </button>
//                             </div>
//                         </div>
//                     </form>
//                 )}

//                 {isManagement && (
//                     <div className="cayep-classroom-teacher-notice">
//                         <div className="cayep-classroom-teacher-notice-icon">
//                             <FaChalkboardTeacher />
//                         </div>

//                         <div>
//                             <strong>
//                                 Facilitation
//                                 Discussion
//                             </strong>

//                             <p>
//                                 Respond to
//                                 learner
//                                 questions and
//                                 contribute
//                                 guidance
//                                 whenever
//                                 clarification
//                                 or another
//                                 perspective
//                                 will help.
//                             </p>
//                         </div>
//                     </div>
//                 )}

//                 {questions.length ===
//                 0 ? (
//                     <div className="cayep-classroom-empty-panel">
//                         <FaUsers />

//                         <h4>
//                             {isManagement
//                                 ? "No Learner Discussions Yet"
//                                 : "Start the Conversation"}
//                         </h4>

//                         <p>
//                             {isManagement
//                                 ? "Learner questions and ideas about this video will appear here."
//                                 : "Be the first learner to ask a question or share an idea about this video."}
//                         </p>
//                     </div>
//                 ) : (
//                     <div className="cayep-classroom-question-list">
//                         {questions.map(
//                             (
//                                 question,
//                                 questionIndex
//                             ) => {
//                                 const replies =
//                                     getQuestionReplies(
//                                         question
//                                     );

//                                 const questionKey =
//                                     question?.id ||
//                                     `question-${questionIndex}`;

//                                 const expanded =
//                                     Boolean(
//                                         expandedQuestions[
//                                             questionKey
//                                         ]
//                                     );

//                                 const replySaving =
//                                     String(
//                                         savingReplyId
//                                     ) ===
//                                     String(
//                                         question?.id
//                                     );

//                                 return (
//                                     <article
//                                         className="cayep-community-thread"
//                                         key={
//                                             questionKey
//                                         }
//                                     >
//                                         <div className="cayep-community-question-row">
//                                             <div className="cayep-community-avatar">
//                                                 <img
//                                                     src={getQuestionAvatar(
//                                                         question
//                                                     )}
//                                                     alt=""
//                                                     loading="lazy"
//                                                     onError={(
//                                                         event
//                                                     ) => {
//                                                         event.currentTarget.src =
//                                                             defaultAvatar;
//                                                     }}
//                                                 />
//                                             </div>

//                                             <div className="cayep-community-message-area">
//                                                 <div className="cayep-community-author-line">
//                                                     <strong>
//                                                         {getQuestionAuthor(
//                                                             question
//                                                         )}
//                                                     </strong>

//                                                     <span>
//                                                         {formatDate(
//                                                             question?.created_at ||
//                                                                 question?.createdAt
//                                                         )}
//                                                     </span>
//                                                 </div>

//                                                 <div className="cayep-community-question-bubble">
//                                                     <span className="cayep-community-quote-mark">
//                                                         “
//                                                     </span>

//                                                     <p>
//                                                         {
//                                                             question?.question
//                                                         }
//                                                     </p>
//                                                 </div>

//                                                 <div className="cayep-community-message-actions">
//                                                     <button
//                                                         type="button"
//                                                         onClick={() =>
//                                                             toggleQuestion(
//                                                                 questionKey
//                                                             )
//                                                         }
//                                                     >
//                                                         <FaReply />

//                                                         {expanded
//                                                             ? "Hide Discussion"
//                                                             : `Join Discussion${
//                                                                   replies.length
//                                                                       ? ` (${replies.length})`
//                                                                       : ""
//                                                               }`}
//                                                     </button>
//                                                 </div>
//                                             </div>
//                                         </div>

//                                         {expanded && (
//                                             <div className="cayep-community-conversation">
//                                                 {replies.length >
//                                                 0 ? (
//                                                     replies.map(
//                                                         (
//                                                             reply,
//                                                             replyIndex
//                                                         ) => {
//                                                             const managementReply =
//                                                                 isReplyFromManagement(
//                                                                     reply
//                                                                 );

//                                                             return (
//                                                                 <div
//                                                                     className={`cayep-community-reply-row ${
//                                                                         managementReply
//                                                                             ? "management-reply"
//                                                                             : "student-reply"
//                                                                     }`}
//                                                                     key={
//                                                                         reply?.id ||
//                                                                         replyIndex
//                                                                     }
//                                                                 >
//                                                                     <div className="cayep-community-reply-avatar">
//                                                                         <img
//                                                                             src={getReplyAvatar(
//                                                                                 reply
//                                                                             )}
//                                                                             alt=""
//                                                                             loading="lazy"
//                                                                             onError={(
//                                                                                 event
//                                                                             ) => {
//                                                                                 event.currentTarget.src =
//                                                                                     defaultAvatar;
//                                                                             }}
//                                                                         />
//                                                                     </div>

//                                                                     <div className="cayep-community-reply-area">
//                                                                         <div className="cayep-community-reply-meta">
//                                                                             <strong>
//                                                                                 {getReplyAuthor(
//                                                                                     reply
//                                                                                 )}
//                                                                             </strong>

//                                                                             <span
//                                                                                 className={`cayep-community-role-badge ${
//                                                                                     managementReply
//                                                                                         ? "facilitator"
//                                                                                         : "learner"
//                                                                                 }`}
//                                                                             >
//                                                                                 {managementReply ? (
//                                                                                     <>
//                                                                                         <FaChalkboardTeacher />
//                                                                                         Facilitator
//                                                                                     </>
//                                                                                 ) : (
//                                                                                     <>
//                                                                                         <FaUser />
//                                                                                         Learner
//                                                                                     </>
//                                                                                 )}
//                                                                             </span>

//                                                                             <span className="cayep-community-reply-date">
//                                                                                 {formatDate(
//                                                                                     reply?.created_at ||
//                                                                                         reply?.createdAt
//                                                                                 )}
//                                                                             </span>
//                                                                         </div>

//                                                                         <div className="cayep-community-reply-bubble">
//                                                                             <p>
//                                                                                 {
//                                                                                     reply?.reply
//                                                                                 }
//                                                                             </p>
//                                                                         </div>
//                                                                     </div>
//                                                                 </div>
//                                                             );
//                                                         }
//                                                     )
//                                                 ) : (
//                                                     <div className="cayep-community-no-replies">
//                                                         <FaComment />

//                                                         <span>
//                                                             No
//                                                             replies
//                                                             yet.
//                                                             Start
//                                                             the
//                                                             conversation
//                                                             and
//                                                             share
//                                                             your
//                                                             perspective.
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {(isStudent ||
//                                                     isManagement) && (
//                                                     <form
//                                                         className="cayep-community-reply-form"
//                                                         onSubmit={(
//                                                             event
//                                                         ) =>
//                                                             submitReply(
//                                                                 event,
//                                                                 question?.id
//                                                             )
//                                                         }
//                                                     >
//                                                         <div className="cayep-community-reply-form-avatar">
//                                                             {isManagement ? (
//                                                                 <FaChalkboardTeacher />
//                                                             ) : (
//                                                                 <FaUser />
//                                                             )}
//                                                         </div>

//                                                         <div className="cayep-community-reply-form-main">
//                                                             <textarea
//                                                                 value={
//                                                                     replyTexts[
//                                                                         question?.id
//                                                                     ] ||
//                                                                     ""
//                                                                 }
//                                                                 onChange={(
//                                                                     event
//                                                                 ) =>
//                                                                     setReplyTexts(
//                                                                         (
//                                                                             previous
//                                                                         ) => ({
//                                                                             ...previous,
//                                                                             [question?.id]:
//                                                                                 event
//                                                                                     .target
//                                                                                     .value,
//                                                                         })
//                                                                     )
//                                                                 }
//                                                                 placeholder={
//                                                                     isManagement
//                                                                         ? "Share guidance, an explanation, or another perspective..."
//                                                                         : "Reply to this learner..."
//                                                                 }
//                                                                 rows={3}
//                                                                 maxLength={
//                                                                     3000
//                                                                 }
//                                                                 disabled={
//                                                                     replySaving
//                                                                 }
//                                                             />

//                                                             <div className="cayep-community-reply-form-footer">
//                                                                 <span>
//                                                                     {isManagement
//                                                                         ? "Facilitator response"
//                                                                         : "Learner response"}
//                                                                 </span>

//                                                                 <button
//                                                                     type="submit"
//                                                                     disabled={
//                                                                         replySaving ||
//                                                                         !String(
//                                                                             replyTexts[
//                                                                                 question?.id
//                                                                             ] ||
//                                                                                 ""
//                                                                         ).trim()
//                                                                     }
//                                                                 >
//                                                                     <FaPaperPlane />

//                                                                     {replySaving
//                                                                         ? "Sending..."
//                                                                         : "Reply"}
//                                                                 </button>
//                                                             </div>
//                                                         </div>
//                                                     </form>
//                                                 )}
//                                             </div>
//                                         )}
//                                     </article>
//                                 );
//                             }
//                         )}
//                     </div>
//                 )}
//             </section>
//         </>
//     );
// };

// export default QuestionAndAnswer;